import {
  AiHelpAskContent,
  AiHelpAskInputType,
  AiHelpAskResponse,
  AiHelpClarifyContent,
  AiHelpClarifyItem,
  AiHelpClarifyOptionGroup,
  AiHelpConfirmContent,
  AiHelpConfirmSummaryObject,
  AiHelpConfirmSummaryValue,
  AiHelpDataBlock,
  AiHelpDataContent,
  AiHelpDataEmailPreviewContent,
  AiHelpDataItemListBlock,
  AiHelpDataItemListItem,
  AiHelpDataItemListOverProject,
  AiHelpDataItemListTicket,
  AiHelpDataKeyValueRow,
  AiHelpDataKeyValuesBlock,
  AiHelpDataRankingBlock,
  AiHelpDataSectionBlock,
  AiHelpDataStatCardItem,
  AiHelpDataStatCardsBlock,
  AiHelpDataStatTone,
  AiHelpDataTableBlock,
  AiHelpDataTableColumn,
  AiHelpDataTextBlock,
  AiHelpDataTextInlineRun,
  AiHelpDataTextSegment,
  AiHelpDataCompletedItem,
  AiHelpDataCompletedItemGroup,
  AiHelpDataCompletedItemsContent,
  AiHelpDataCurrentWorkContent,
  AiHelpDataCurrentWorkGroup,
  AiHelpDataCurrentWorkItem,
  AiHelpDataOverdueTasksContent,
  AiHelpDataOverdueReqsIssuesContent,
  AiHelpDataOverdueReqIssueItem,
  AiHelpDataPriorityTasksContent,
  AiHelpDataPendingServiceTasksContent,
  AiHelpDataPendingServiceTaskItem,
  AiHelpDataItemsDueContent,
  AiHelpDataItemsDueGroup,
  AiHelpDataItemsDueItem,
  AiHelpDataWorkTodayContent,
  AiHelpDataWorkTodayGroup,
  AiHelpDataWorkTodayItem,
  AiHelpDataProjectTaskBreakdownContent,
  AiHelpDataProjectTaskBreakdownItem,
  AiHelpDataProjectHealthContent,
  AiHelpDataClientBriefContent,
  AiHelpDataClientBriefProjectItem,
  AiHelpDataClientRequestItem,
  AiHelpDataClientRequestsContent,
  AiHelpDataProjectItem,
  AiHelpDataProjectsOverEstimateContent,
  AiHelpDataProjectsOverEstimateItem,
  AiHelpDataCommentSearchContent,
  AiHelpDataCommentSearchItem,
  AiHelpDataMyApprovalGroup,
  AiHelpDataMyApprovalItem,
  AiHelpDataMyApprovalsContent,
  AiHelpDataRankingContent,
  AiHelpDataRankingItem,
  AiHelpDataUpdateItem,
  AiHelpDataValue,
  AiHelpGuideContent,
  AiHelpMessageContent,
  AiHelpOffTopicContent,
  AiHelpReplyAction,
  AiHelpGuideMethod,
  AiHelpGuideStep
} from '../ai-help-assistant';

const FALLBACK_MESSAGE = 'Sorry, I could not find an answer. Please try again.';

/** Parse unknown helpdesk API payloads into a normalized assistant message. */
export function parseAiHelpResponse(raw: unknown): AiHelpAskResponse {
  const confirm = extractConfirm(raw);
  if (confirm) {
    return { content: confirm };
  }

  const ask = extractAsk(raw);
  if (ask) {
    return { content: ask };
  }

  const clarify = extractClarify(raw);
  if (clarify) {
    return { content: clarify };
  }

  const data = extractData(raw);
  if (data) {
    return { content: data };
  }

  const guide = extractGuide(raw);
  if (guide) {
    return { content: guide };
  }

  const offTopic = extractOffTopic(raw);
  if (offTopic) {
    return { content: offTopic };
  }

  const text = extractPlainText(raw);
  if (text) {
    return { content: { kind: 'text', text, isAudio: extractIsAudio(raw) } };
  }

  // Prefer a real intro over the generic fallback when structured data could not be parsed
  // (e.g. empty project_list with only an intro message).
  const intro = extractIntroText(raw);
  if (intro) {
    return { content: { kind: 'text', text: intro, isAudio: extractIsAudio(raw) } };
  }

  return { content: { kind: 'text', text: FALLBACK_MESSAGE } };
}

/** Flatten guide content into plain text for conversation history. */
export function serializeGuideContent(content: AiHelpGuideContent): string {
  const lines: string[] = [];

  if (content.intro.trim()) {
    lines.push(content.intro.trim());
  }

  content.methods.forEach((method, methodIndex) => {
    if (method.label?.trim()) {
      lines.push(method.label.trim());
    }

    method.steps.forEach((step, stepIndex) => {
      const prefix = `${stepIndex + 1}. `;
      const linkSuffix = step.link ? ` (${step.link.label}: ${step.link.url})` : '';
      lines.push(`${prefix}${step.text.trim()}${linkSuffix}`);
    });

    if (methodIndex < content.methods.length - 1) {
      lines.push('');
    }
  });

  if (content.note?.trim()) {
    lines.push(content.note.trim());
  }

  return lines.join('\n').trim();
}

/** Keys kept on the data payload for logic, but never shown in the reply UI. */
const HIDDEN_DATA_KEYS = new Set([
  'kind',
  'blocks',
  'reply_type',
  'truncated',
  'obj_type',
  'objtype',
  'band',
  'has_more',
  'item_type',
  'ranged',
  'scope_label',
  'keyword',
  'people_count',
  'item_label',
  'top',
  'min_percent',
  'matches',
  'approvals',
  'shown',
  'has_work',
  'do_now',
  'coming_up',
  'on_date',
  'isAudio'
]);

/** Whether a data-record key should appear in the visible / spoken reply. */
export function isDisplayableDataKey(key: string): boolean {
  return !HIDDEN_DATA_KEYS.has(key.trim().toLowerCase());
}

/** Flatten structured data replies into plain text for the chat bubble / speech. */
export function serializeDataContent(content: AiHelpDataContent): string {
  const lines: string[] = [];

  if (content.intro.trim()) {
    lines.push(content.intro.trim());
  }

  const preferProjectList =
    content.dataKind === 'project_list' && (content.projects?.length ?? 0) > 0;

  if (!preferProjectList && content.blocks?.length) {
    const blockItemLabel = resolveBlocksItemLabel(content.blocks);
    content.blocks.forEach((block) => {
      if (block.type === 'stat_cards') {
        if (block.title?.trim()) {
          lines.push(block.title.trim());
        }
        block.items.forEach((item) => {
          lines.push(`${item.label}: ${formatDataBlockStatValue(item.value)}`);
        });
        return;
      }
      if (block.type === 'item_list') {
        if (block.title?.trim()) {
          lines.push(block.title.trim());
        }
        block.items.forEach((item) => {
          lines.push(formatItemListItemLine(item));
          item.tickets?.forEach((ticket) => {
            lines.push(`  - ${formatItemListTicketLine(ticket)}`);
          });
          item.overProjects?.forEach((project) => {
            lines.push(`  - ${formatItemListOverProjectLine(project)}`);
          });
        });
        return;
      }
      if (block.type === 'ranking') {
        if (block.title?.trim()) {
          lines.push(block.title.trim());
        }
        block.items.forEach((entry, index) => {
          const value = formatRankingEntryValue(entry, blockItemLabel);
          const share = entry.percent != null ? ` (${formatPercentComplete(entry.percent)})` : '';
          lines.push(`${index + 1}. ${entry.name}: ${value}${share}`);
          entry.byTask?.forEach((task) => {
            const typeSuffix = task.type ? ` (${task.type})` : '';
            lines.push(`  - ${task.item}${typeSuffix}: ${formatHours(task.hours)}`);
          });
        });
        return;
      }
      if (block.type === 'key_values') {
        if (block.title?.trim()) {
          lines.push(block.title.trim());
        }
        block.rows.forEach((row) => {
          lines.push(`${formatKeyValueLabel(row.label)}: ${row.value}`);
        });
        return;
      }
      if (block.type === 'table') {
        if (block.title?.trim()) {
          lines.push(block.title.trim());
        }
        const headers = block.columns.map((col) => col.fieldName).join(' | ');
        if (headers) {
          lines.push(headers);
        }
        block.rows.forEach((row) => {
          lines.push(
            block.columns.map((col) => row[col.prop]?.trim() || '—').join(' | ')
          );
        });
        return;
      }
      if (block.type === 'section') {
        if (block.heading?.trim()) {
          lines.push(block.heading.trim());
        }
        const spoken = stripMarkdownEmphasis(block.text);
        if (spoken) {
          lines.push(spoken);
        }
        return;
      }
      const spoken = stripMarkdownEmphasis(block.text);
      if (spoken) {
        lines.push(spoken);
      }
    });
    return lines.join('\n').trim();
  }

  if (content.dataKind === 'projects_over_estimate' && content.projectsOverEstimateContent) {
    const estimate = content.projectsOverEstimateContent;
    if (estimate.count != null) {
      lines.push(`Count: ${estimate.count}`);
    }
    if (estimate.overPercent != null) {
      lines.push(`Over threshold: ${formatPercentComplete(estimate.overPercent)}`);
    }

    estimate.matches.forEach((project) => {
      const parts = [project.projectName];
      if (project.owner) {
        parts.push(`Owner: ${project.owner}`);
      }
      if (project.estimatedHours != null) {
        parts.push(`Estimated: ${formatHours(project.estimatedHours)}`);
      }
      if (project.actualHours != null) {
        parts.push(`Actual: ${formatHours(project.actualHours)}`);
      }
      if (project.overrunPercent != null) {
        parts.push(`Overrun: ${formatPercentComplete(project.overrunPercent)}`);
      }
      lines.push(parts.join(' — '));
    });
    return lines.join('\n').trim();
  }

  if (content.dataKind === 'project_list') {
    content.projects?.forEach((project) => {
      const parts = [project.projectName];
      if (project.status) {
        parts.push(project.status);
      }
      if (project.daysOverdue != null) {
        parts.push(formatDaysOverdue(project.daysOverdue));
      }
      if (project.overdueCount != null) {
        parts.push(formatOverdueCount(project.overdueCount));
      }
      if (project.dueDate) {
        parts.push(`Due: ${formatDailyHoursDate(project.dueDate)}`);
      }
      if (project.projectedCompletion) {
        parts.push(`Projected: ${formatDailyHoursDate(project.projectedCompletion)}`);
      }
      if (project.percentComplete != null) {
        parts.push(`${formatPercentComplete(project.percentComplete)} complete`);
      }
      if (project.owner) {
        parts.push(`Owner: ${project.owner}`);
      }
      lines.push(parts.join(' — '));
    });
    return lines.join('\n').trim();
  }

  if (content.dataKind === 'project_health' && content.projectHealthContent) {
    const health = content.projectHealthContent;
    lines.push(`Project: ${health.projectName}`);
    if (health.status) {
      lines.push(`Status: ${health.status}`);
    }
    if (health.owner) {
      lines.push(`Owner: ${health.owner}`);
    }
    if (health.dueDate) {
      lines.push(`Due: ${formatDailyHoursDate(health.dueDate)}`);
    }
    if (health.originalDueDate && health.originalDueDate !== health.dueDate) {
      lines.push(`Original due: ${formatDailyHoursDate(health.originalDueDate)}`);
    }
    if (health.percentComplete != null) {
      lines.push(`${formatPercentComplete(health.percentComplete)} complete`);
    }
    if (health.daysRemaining != null && !isCompleteProjectHealth(health)) {
      lines.push(formatDaysRemaining(health.daysRemaining));
    }
    if (health.openTaskCount != null) {
      lines.push(`Open tasks: ${health.openTaskCount}`);
    }
    if (health.hours) {
      if (health.hours.totalHours != null) {
        lines.push(`Total hours: ${formatHours(health.hours.totalHours)}`);
      }
      if (health.hours.loggedHours != null) {
        lines.push(`Logged hours: ${formatHours(health.hours.loggedHours)}`);
      }
      if (health.hours.pendingHours != null) {
        lines.push(`Pending hours: ${formatHours(health.hours.pendingHours)}`);
      }
    }
    return lines.join('\n').trim();
  }

  if (content.dataKind === 'project_tasks' && content.projectTasksContent) {
    if (content.projectTasksContent.projectName) {
      lines.push(`Project: ${content.projectTasksContent.projectName}`);
    }

    content.projectTasksContent.tasks.forEach((task) => {
      const parts = [task.taskName];
      if (task.status) {
        parts.push(task.status);
      }
      if (task.due) {
        parts.push(`Due: ${formatDailyHoursDate(task.due)}`);
      }
      if (task.assignees) {
        parts.push(`Assignees: ${task.assignees}`);
      }
      lines.push(parts.join(' — '));
    });
    return lines.join('\n').trim();
  }

  if (content.dataKind === 'project_task_breakdown' && content.projectTaskBreakdownContent) {
    const breakdown = content.projectTaskBreakdownContent;
    if (breakdown.projectName) {
      lines.push(`Project: ${breakdown.projectName}`);
    }
    if (breakdown.totalTasks != null) {
      lines.push(`Total tasks: ${breakdown.totalTasks}`);
    }
    if (breakdown.completedTasks != null) {
      lines.push(`Completed tasks: ${breakdown.completedTasks}`);
    }
    if (breakdown.remainingTasks != null) {
      lines.push(`Remaining tasks: ${breakdown.remainingTasks}`);
    }

    breakdown.remaining.forEach((task) => {
      const parts = [task.taskName];
      if (task.status) {
        parts.push(task.status);
      }
      if (task.percentComplete != null) {
        parts.push(`${formatPercentComplete(task.percentComplete)} complete`);
      }
      if (task.assignees) {
        parts.push(`Assignees: ${task.assignees}`);
      }
      lines.push(parts.join(' — '));
    });
    return lines.join('\n').trim();
  }

  if (content.dataKind === 'overdue_tasks' && content.overdueTasksContent) {
    if (content.overdueTasksContent.scopeLabel) {
      lines.push(`Scope: ${content.overdueTasksContent.scopeLabel}`);
    }
    if (content.overdueTasksContent.count != null) {
      lines.push(`Count: ${content.overdueTasksContent.count}`);
    }
    if (content.overdueTasksContent.shown != null) {
      lines.push(`Showing: ${content.overdueTasksContent.shown}`);
    }

    content.overdueTasksContent.tasks.forEach((task) => {
      const parts = [task.taskName];
      if (task.status) {
        parts.push(task.status);
      }
      if (task.daysOverdue != null) {
        parts.push(formatDaysOverdue(task.daysOverdue));
      }
      if (task.dueDate) {
        parts.push(`Due: ${formatDailyHoursDate(task.dueDate)}`);
      }
      if (task.project) {
        parts.push(`Project: ${task.project}`);
      }
      if (task.assignees) {
        parts.push(`Assignees: ${task.assignees}`);
      }
      lines.push(parts.join(' — '));
    });
    return lines.join('\n').trim();
  }

  if (content.dataKind === 'overdue_reqs_issues' && content.overdueReqsIssuesContent) {
    const payload = content.overdueReqsIssuesContent;
    if (payload.itemType) {
      lines.push(`Type: ${formatDataLabel(payload.itemType)}`);
    }
    if (payload.scopeLabel) {
      lines.push(`Scope: ${payload.scopeLabel}`);
    }
    if (payload.count != null) {
      lines.push(`Count: ${payload.count}`);
    }

    payload.items.forEach((item) => {
      const parts = [item.name];
      if (item.status) {
        parts.push(item.status);
      }
      if (item.daysOverdue != null) {
        parts.push(formatDaysOverdue(item.daysOverdue));
      }
      if (item.dueDate) {
        parts.push(`Due: ${formatDailyHoursDate(item.dueDate)}`);
      }
      if (item.project) {
        parts.push(`Project: ${item.project}`);
      }
      lines.push(parts.join(' — '));
    });

    if (payload.truncated) {
      lines.push('List is truncated.');
    }
    return lines.join('\n').trim();
  }

  if (content.dataKind === 'priority_tasks' && content.priorityTasksContent) {
    if (content.priorityTasksContent.scopeLabel) {
      lines.push(`Scope: ${content.priorityTasksContent.scopeLabel}`);
    }
    if (content.priorityTasksContent.count != null) {
      lines.push(`Count: ${content.priorityTasksContent.count}`);
    }

    content.priorityTasksContent.tasks.forEach((task) => {
      const parts = [task.name];
      if (task.status) {
        parts.push(task.status);
      }
      if (task.dueDate) {
        parts.push(`Due: ${formatDailyHoursDate(task.dueDate)}`);
      }
      if (task.project) {
        parts.push(`Project: ${task.project}`);
      }
      if (task.assignees) {
        parts.push(`Assignees: ${task.assignees}`);
      }
      lines.push(parts.join(' — '));
    });
    return lines.join('\n').trim();
  }

  if (content.dataKind === 'my_pending_service_tasks' && content.pendingServiceTasksContent) {
    const payload = content.pendingServiceTasksContent;
    if (payload.user) {
      lines.push(`User: ${payload.user}`);
    }
    if (payload.serviceGroup) {
      lines.push(`Service group: ${payload.serviceGroup}`);
    }
    if (payload.count != null) {
      lines.push(`Count: ${payload.count}`);
    }
    if (payload.shown != null) {
      lines.push(`Showing: ${payload.shown}`);
    }

    payload.tasks.forEach((task) => {
      const parts = [task.taskName];
      if (task.status) {
        parts.push(task.status);
      }
      if (task.dueDate) {
        parts.push(`Due: ${formatDailyHoursDate(task.dueDate)}`);
      }
      if (task.projectName) {
        parts.push(`Project: ${task.projectName}`);
      }
      if (task.serviceName) {
        parts.push(`Service: ${task.serviceName}`);
      }
      lines.push(parts.join(' — '));
    });

    if (payload.truncated) {
      lines.push('List is truncated.');
    }
    return lines.join('\n').trim();
  }

  if (content.dataKind === 'items_due' && content.itemsDueContent) {
    const due = content.itemsDueContent;
    if (due.count != null) {
      lines.push(`Count: ${due.count}`);
    }

    due.items.forEach((item) => {
      const parts = [item.name];
      if (item.type) {
        parts.push(formatDataLabel(item.type));
      }
      if (item.status) {
        parts.push(item.status);
      }
      if (item.dueDate) {
        parts.push(`Due: ${formatDailyHoursDate(item.dueDate)}`);
      }
      if (item.project) {
        parts.push(`Project: ${item.project}`);
      }
      if (item.assignees) {
        parts.push(`Assignees: ${item.assignees}`);
      }
      lines.push(parts.join(' — '));
    });
    return lines.join('\n').trim();
  }

  if (content.dataKind === 'work_today' && content.workTodayContent) {
    const workToday = content.workTodayContent;
    if (workToday.doNow.length === 0 && workToday.comingUp.length === 0) {
      return lines.join('\n').trim();
    }

    const appendWorkTodayItems = (label: string, items: AiHelpDataWorkTodayItem[]): void => {
      if (!items.length) {
        return;
      }
      lines.push(`${label}:`);
      items.forEach((item) => {
        const parts = [item.name];
        if (item.type) {
          parts.push(formatDataLabel(item.type));
        }
        if (item.status) {
          parts.push(item.status);
        }
        if (item.dueDate) {
          parts.push(`Due: ${formatDailyHoursDate(item.dueDate)}`);
        }
        if (item.project) {
          parts.push(`Project: ${item.project}`);
        }
        if (item.daysOverdue != null) {
          parts.push(formatDaysOverdue(item.daysOverdue));
        }
        lines.push(parts.join(' — '));
      });
    };

    appendWorkTodayItems('Do now', workToday.doNow);
    appendWorkTodayItems('Coming up', workToday.comingUp);
    return lines.join('\n').trim();
  }

  if (content.dataKind === 'completed_items' && content.completedItemsContent) {
    const completed = content.completedItemsContent;
    if (completed.team) {
      lines.push(`Team: ${completed.team}`);
    }
    if (completed.totalCompleted != null) {
      lines.push(`Total completed: ${completed.totalCompleted}`);
    }

    completed.groups.forEach((group) => {
      lines.push(`${formatDataLabel(group.type)}:`);
      group.items.forEach((item) => {
        const parts = [item.name];
        if (item.status) {
          parts.push(item.status);
        }
        if (item.completedOn) {
          parts.push(`Completed: ${formatDailyHoursDate(item.completedOn)}`);
        }
        if (item.assignees) {
          parts.push(`Assignees: ${item.assignees}`);
        }
        lines.push(parts.join(' — '));
      });
    });
    return lines.join('\n').trim();
  }

  if (content.dataKind === 'current_work' && content.currentWorkContent) {
    const currentWork = content.currentWorkContent;
    if (currentWork.user) {
      lines.push(`User: ${currentWork.user}`);
    }
    if (currentWork.count != null) {
      lines.push(`Items: ${currentWork.count}`);
    }

    currentWork.items.forEach((item) => {
      const type = item.type ? formatDataLabel(item.type) : '';
      lines.push(type ? `${item.name} — ${type}` : item.name);
    });
    return lines.join('\n').trim();
  }

  if (content.dataKind === 'updates' && content.updatesContent) {
    if (content.updatesContent.user) {
      lines.push(`User: ${content.updatesContent.user}`);
    }
    if (content.updatesContent.count != null) {
      lines.push(`Updates: ${content.updatesContent.count}`);
    }

    const items = content.updatesContent.updates ?? [];
    items.forEach((u) => {
      const on = u.on ? ` ${u.on}` : '';
      const date = u.date ? ` (${u.date})` : '';
      const text = u.text ? `: ${u.text}` : '';
      lines.push(`${u.type ? `${u.type}${on}${date}` : `${on.trim()}${date}`}${text}`.trim());
    });
    return lines.join('\n').trim();
  }

  if (content.dataKind === 'comment_search' && content.commentSearchContent) {
    const search = content.commentSearchContent;
    if (search.keyword) {
      lines.push(`Keyword: ${search.keyword}`);
    }
    if (search.total != null) {
      lines.push(`Total: ${search.total}`);
    }

    search.matches.forEach((match) => {
      const title = match.on || match.type || 'Comment';
      const project = match.project ? ` · ${match.project}` : '';
      const by = match.by ? ` by ${match.by}` : '';
      const date = match.date ? ` (${match.date})` : '';
      const snippet = match.snippet ? `: ${match.snippet}` : '';
      lines.push(`${title}${project}${by}${date}${snippet}`.trim());
    });
    return lines.join('\n').trim();
  }

  if (content.dataKind === 'my_approvals' && content.myApprovalsContent) {
    const approvals = content.myApprovalsContent;
    if (approvals.total != null) {
      lines.push(`Total: ${approvals.total}`);
    }
    if (approvals.shown != null) {
      lines.push(`Shown: ${approvals.shown}`);
    }

    approvals.approvals.forEach((item) => {
      const parts = [item.title];
      if (item.type) {
        parts.push(formatDataLabel(item.type));
      }
      if (item.project) {
        parts.push(`Project: ${item.project}`);
      }
      if (item.requestedOn) {
        parts.push(`Requested: ${formatDailyHoursDate(item.requestedOn)}`);
      }
      lines.push(parts.join(' — '));
    });
    return lines.join('\n').trim();
  }

  if (content.dataKind === 'client_requests' && content.clientRequestsContent) {
    const payload = content.clientRequestsContent;
    if (payload.client) {
      lines.push(`Client: ${payload.client}`);
    }
    if (payload.count != null) {
      lines.push(`Count: ${payload.count}`);
    }

    payload.requests.forEach((item) => {
      const parts = [item.request];
      if (item.status) {
        parts.push(item.status);
      }
      if (item.project) {
        parts.push(`Project: ${item.project}`);
      }
      if (item.dueDate) {
        parts.push(`Due: ${formatDailyHoursDate(item.dueDate)}`);
      }
      lines.push(parts.join(' — '));
    });

    if (payload.truncated) {
      lines.push('List is truncated.');
    }
    return lines.join('\n').trim();
  }

  if (content.dataKind === 'ranking' && content.rankingContent) {
    // Empty ranking: intro alone is the answer (e.g. nobody matched).
    if (content.rankingContent.ranking.length === 0) {
      return lines.join('\n').trim();
    }

    if (content.rankingContent.scope) {
      lines.push(`Scope: ${content.rankingContent.scope}`);
    }
    if (content.rankingContent.peopleCounted != null) {
      const countLabel = content.rankingContent.countLabel ?? 'People counted';
      lines.push(`${countLabel}: ${content.rankingContent.peopleCounted}`);
    }

    content.rankingContent.ranking.forEach((entry, index) => {
      const value = formatRankingEntryValue(entry, content.rankingContent?.itemLabel);
      const share = entry.percent != null ? ` (${formatPercentComplete(entry.percent)})` : '';
      lines.push(`${index + 1}. ${entry.name} — ${value}${share}`);
    });
    return lines.join('\n').trim();
  }

  if (content.dataKind === 'user' && content.userContent) {
    if (content.userContent.user) {
      lines.push(`User: ${content.userContent.user}`);
    }
    if (content.userContent.totalHours != null) {
      lines.push(`Total hours: ${formatHours(content.userContent.totalHours)}`);
    }

    content.userContent.byTask.forEach((entry) => {
      const type = entry.type ? ` (${entry.type})` : '';
      lines.push(`${entry.item}${type} — ${formatHours(entry.hours)}`);
    });
    return lines.join('\n').trim();
  }

  if (content.dataKind === 'daily_hours' && content.dailyHoursContent) {
    if (content.dailyHoursContent.user) {
      lines.push(`User: ${content.dailyHoursContent.user}`);
    }
    if (content.dailyHoursContent.totalHours != null) {
      lines.push(`Total hours: ${formatHours(content.dailyHoursContent.totalHours)}`);
    }
    if (content.dailyHoursContent.dayCount != null) {
      lines.push(`Days: ${content.dailyHoursContent.dayCount}`);
    }

    content.dailyHoursContent.days.forEach((entry) => {
      lines.push(`${formatDailyHoursDate(entry.date)} — ${formatHours(entry.hours)}`);
    });

    return lines.join('\n').trim();
  }

  if (content.dataKind === 'ai_hours_trend' && content.aiHoursTrendContent) {
    if (content.aiHoursTrendContent.scopeLabel) {
      lines.push(`Scope: ${content.aiHoursTrendContent.scopeLabel}`);
    }
    if (content.aiHoursTrendContent.totalHours != null) {
      lines.push(`Total hours: ${formatHours(content.aiHoursTrendContent.totalHours)}`);
    }
    if (content.aiHoursTrendContent.weeksCount != null) {
      lines.push(`Weeks: ${content.aiHoursTrendContent.weeksCount}`);
    }

    content.aiHoursTrendContent.weeks.forEach((entry) => {
      const label = entry.weekLabel || formatDailyHoursDate(entry.weekStart);
      lines.push(`${label} — ${formatHours(entry.hours)}`);
    });

    return lines.join('\n').trim();
  }

  if (content.dataKind === 'leave' && content.leaveContent) {
    if (content.leaveContent.people.length === 0) {
      return lines.join('\n').trim();
    }

    if (content.leaveContent.scope) {
      lines.push(`Scope: ${content.leaveContent.scope}`);
    }
    if (content.leaveContent.peopleCounted != null) {
      lines.push(`People counted: ${content.leaveContent.peopleCounted}`);
    }

    content.leaveContent.people.forEach((person) => {
      lines.push(`${person.name} — ${formatHours(person.totalHours)}`);
      person.days.forEach((day) => {
        const category = day.category ? ` (${day.category})` : '';
        lines.push(`  ${formatDailyHoursDate(day.date)}${category} — ${formatHours(day.hours)}`);
      });
    });
    return lines.join('\n').trim();
  }

  if (content.dataKind === 'free_hours' && content.freeHoursContent) {
    const freeHours = content.freeHoursContent;
    if (freeHours.target) {
      const roleSuffix = freeHours.isRole ? ' (role)' : '';
      lines.push(`Target: ${freeHours.target}${roleSuffix}`);
    }
    if (freeHours.date) {
      lines.push(`Date: ${freeHours.date}`);
    }
    if (freeHours.capacityHours != null) {
      lines.push(`Capacity: ${formatHours(freeHours.capacityHours)}`);
    }

    freeHours.people.forEach((person) => {
      const overbooked = person.overbooked ? ' (overbooked)' : '';
      lines.push(
        `${person.name} — scheduled ${formatHours(person.scheduledHours)}, free ${formatHours(person.freeHours)}${overbooked}`
      );
    });
    return lines.join('\n').trim();
  }

  if (content.dataKind === 'timesheet_submission' && content.timesheetSubmissionContent) {
    const submission = content.timesheetSubmissionContent;
    if (submission.team) {
      lines.push(`Team: ${submission.team}`);
    }
    if (submission.week) {
      lines.push(
        `Week: ${formatDailyHoursDate(submission.week.from)} to ${formatDailyHoursDate(submission.week.to)}`
      );
    }
    if (submission.totalMembers != null) {
      lines.push(`Total members: ${submission.totalMembers}`);
    }
    if (submission.submittedCount != null) {
      lines.push(`Submitted: ${submission.submittedCount}`);
    }
    if (submission.notSubmittedCount != null) {
      lines.push(`Not submitted: ${submission.notSubmittedCount}`);
    }

    submission.notSubmitted.forEach((person) => {
      const status = person.status ? ` (${person.status})` : '';
      lines.push(`${person.name}${status}`);
    });
    return lines.join('\n').trim();
  }

  if (content.dataKind === 'client_brief' && content.clientBriefContent) {
    const brief = content.clientBriefContent;
    if (brief.client) {
      lines.push(`Client: ${brief.client}`);
    }
    if (brief.activeProjectCount != null) {
      lines.push(`Active projects: ${brief.activeProjectCount}`);
    }
    if (brief.overdueTaskCount != null) {
      lines.push(`Overdue tasks: ${brief.overdueTaskCount}`);
    }
    if (brief.hoursThisMonth != null) {
      const month = brief.monthLabel ? ` (${brief.monthLabel})` : '';
      lines.push(`Hours this month${month}: ${formatHours(brief.hoursThisMonth)}`);
    }

    brief.projects.forEach((project) => {
      const parts = [project.projectName];
      if (project.status) {
        parts.push(project.status);
      }
      if (project.percentComplete != null) {
        parts.push(`${formatPercentComplete(project.percentComplete)} complete`);
      }
      if (project.waitingOn) {
        parts.push(`Waiting on: ${project.waitingOn}`);
      }
      lines.push(parts.join(' — '));
    });

    brief.recentActivity.forEach((activity) => {
      lines.push(activity);
    });

    brief.talkingPoints.forEach((point) => {
      lines.push(point);
    });
    return lines.join('\n').trim();
  }

  Object.entries(content.data).forEach(([key, value]) => {
    if (!isDisplayableDataKey(key)) {
      return;
    }
    lines.push(`${formatDataLabel(key)}: ${formatRecordFieldValue(key, value)}`);
  });

  return lines.join('\n').trim();
}

export function serializeMessageContent(content: AiHelpMessageContent): string {
  if (content.kind === 'guide') {
    return serializeGuideContent(content);
  }
  if (content.kind === 'data') {
    return serializeDataContent(content);
  }
  return content.text;
}

/**
 * Serialize structured assistant content for API `history[].content`.
 * Clarify, confirm, ask, guide, and data replies must be JSON.stringified
 * API shapes, not the flattened display text used in the UI.
 */
export function serializeMessageContentForHistory(content: AiHelpMessageContent): string {
  if (content.kind === 'clarify') {
    return JSON.stringify(toClarifyHistoryPayload(content));
  }
  if (content.kind === 'confirm') {
    return JSON.stringify({
      reply_type: 'confirm',
      message: content.text,
      summary: content.summary,
      isAudio: content.isAudio === true
    });
  }
  if (content.kind === 'ask') {
    return JSON.stringify({
      reply_type: 'ask',
      message: content.text,
      isAudio: content.isAudio === true
    });
  }
  if (content.kind === 'off_topic') {
    return JSON.stringify(toOffTopicHistoryPayload(content));
  }
  if (content.kind === 'guide') {
    return JSON.stringify(toGuideHistoryPayload(content));
  }
  if (content.kind === 'data') {
    return JSON.stringify(toDataHistoryPayload(content));
  }
  return content.text;
}

/** API-shaped data reply for `history[].content` (not flattened display text). */
function toDataHistoryPayload(content: AiHelpDataContent): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    reply_type: 'data',
    intro: content.intro,
    isAudio: content.isAudio === true
  };

  if (content.actions?.length) {
    payload['actions'] = content.actions.map((action) => ({
      type: action.type,
      action: action.action,
      label: action.label
    }));
  }

  if (content.blocks?.length) {
    payload['blocks'] = content.blocks.map(toApiDataBlock);
    return payload;
  }

  payload['data'] = content.apiData ?? toDataHistoryData(content);
  return payload;
}

/** Last-resort `data` object when the original API payload was not kept. */
function toDataHistoryData(content: AiHelpDataContent): Record<string, unknown> {
  const data: Record<string, unknown> = {
    kind: content.dataKind,
    ...content.data
  };
  if (content.blocks?.length) {
    data['blocks'] = content.blocks.map(toApiDataBlock);
  }
  return data;
}

function toApiDataBlock(block: AiHelpDataBlock): Record<string, unknown> {
  if (block.type === 'stat_cards') {
    return {
      type: 'stat_cards',
      ...(block.title ? { title: block.title } : {}),
      items: block.items.map((item) => ({
        label: item.label,
        value: item.value,
        tone: item.tone
      }))
    };
  }

  if (block.type === 'item_list') {
    return {
      type: 'item_list',
      ...(block.title ? { title: block.title } : {}),
      items: block.items.map((item) => ({
        title: item.title,
        name: item.title,
        ...(item.priority ? { priority: item.priority } : {}),
        ...(item.status ? { status: item.status } : {}),
        ...(item.assignees ? { assignees: item.assignees } : {}),
        ...(item.link ? { link: item.link } : {}),
        ...(item.originalDate ? { original_date: item.originalDate } : {}),
        ...(item.projectedDate ? { projected_date: item.projectedDate } : {}),
        ...(item.delayBusinessDays != null ? { delay_business_days: item.delayBusinessDays } : {}),
        ...(item.totalHours != null
          ? { total_hours: item.totalHours, hours: item.totalHours }
          : {}),
        ...(item.billableHours != null ? { billable_hours: item.billableHours } : {}),
        ...(item.aiHours != null ? { ai_hours: item.aiHours } : {}),
        ...(item.submitted != null ? { submitted: item.submitted } : {}),
        ...(item.overProjects?.length
          ? {
              over_projects: item.overProjects.map((project) => ({
                project: project.project,
                ...(project.monthlyHours != null ? { monthly_hours: project.monthlyHours } : {}),
                ...(project.usedHours != null ? { used_hours: project.usedHours } : {}),
                ...(project.overage != null ? { overage: project.overage } : {}),
                ...(project.utilizationPct != null ? { utilization_pct: project.utilizationPct } : {})
              }))
            }
          : {}),
        ...(item.tickets?.length
          ? {
              tickets: item.tickets.map((ticket) => ({
                ticket: ticket.ticket,
                ...(ticket.project ? { project: ticket.project } : {}),
                ...(ticket.ticketType ? { ticket_type: ticket.ticketType } : {}),
                ...(ticket.reason ? { reason: ticket.reason } : {}),
                ...(ticket.link ? { link: ticket.link } : {})
              }))
            }
          : {})
      }))
    };
  }

  if (block.type === 'ranking') {
    return {
      type: 'ranking',
      ...(block.title ? { title: block.title } : {}),
      items: block.items.map((item) => ({
        name: item.name,
        ...(item.hours != null ? { hours: item.hours, total_hours: item.hours } : {}),
        ...(item.openItems != null ? { open_items: item.openItems } : {}),
        ...(item.days != null ? { days: item.days } : {}),
        ...(item.type ? { type: item.type } : {}),
        ...(item.percent != null ? { percent: item.percent } : {}),
        ...(item.byTask?.length
          ? {
              by_task: item.byTask.map((task) => ({
                item: task.item,
                ...(task.type ? { type: task.type } : {}),
                hours: task.hours
              }))
            }
          : {})
      }))
    };
  }

  if (block.type === 'key_values') {
    return {
      type: 'key_values',
      ...(block.title ? { title: block.title } : {}),
      rows: block.rows.map((row) => ({
        label: row.label,
        value: row.value
      }))
    };
  }

  if (block.type === 'table') {
    return {
      type: 'table',
      ...(block.title ? { title: block.title } : {}),
      list: block.columns.map((col) => ({
        fieldName: col.fieldName,
        prop: col.prop
      })),
      row: block.rows.map((row) => ({ ...row }))
    };
  }

  if (block.type === 'section') {
    return {
      type: 'section',
      ...(block.heading ? { heading: block.heading } : {}),
      text: block.text
    };
  }

  return {
    type: 'text',
    text: block.text
  };
}

function toClarifyHistoryPayload(content: AiHelpClarifyContent): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    reply_type: 'clarify',
    message: content.text,
    isAudio: content.isAudio === true
  };

  if (content.note) {
    payload['note'] = content.note;
  }

  if (content.choices?.length) {
    payload['options'] = content.choices;
    return payload;
  }

  if (content.options?.length) {
    payload['options'] = content.options.map((group) => ({
      group: group.group,
      items: group.items.map(clarifyItemToApiOption)
    }));
    return payload;
  }

  if (content.items?.length) {
    payload['options'] = content.items.map(clarifyItemToApiOption);
  }

  return payload;
}

/** API-shaped option object for a clarify selection (used in question / history). */
export function clarifyItemToApiOption(item: AiHelpClarifyItem): Record<string, unknown> {
  if (item.kind === 'project') {
    const option: Record<string, unknown> = {
      [item.idKey || 'project_id']: item.projectId,
      name: item.projectName
    };
    if (item.status) {
      option['status'] = item.status;
    }
    if (item.owner) {
      option['owner'] = item.owner;
    }
    return option;
  }

  if (item.kind === 'task') {
    const option: Record<string, unknown> = {
      [item.idKey || 'task_id']: item.taskId,
      name: item.taskName
    };
    if (item.project) {
      option['project'] = item.project;
    }
    if (item.refNo != null) {
      option['ref_no'] = item.refNo;
    }
    if (item.due) {
      option['due'] = item.due;
    }
    if (item.plannedCompletion) {
      option['planned_completion'] = item.plannedCompletion;
    }
    return option;
  }

  if (item.kind === 'person') {
    const option: Record<string, unknown> = {
      user_id: item.userId,
      name: item.name
    };
    if (item.job) {
      option['job'] = item.job;
    }
    return option;
  }

  if (item.kind === 'template') {
    return { template_id: item.templateId, name: item.name };
  }

  if (item.kind === 'approval') {
    return { approval_id: item.approvalId, name: item.name };
  }

  if (item.kind === 'service') {
    return { service_id: item.serviceId, name: item.name };
  }

  return { service_category_id: item.serviceCategoryId, name: item.name };
}

/** JSON string sent as the user question / history entry when a clarify option is picked. */
export function serializeClarifySelection(item: AiHelpClarifyItem): string {
  return JSON.stringify(clarifyItemToApiOption(item));
}

/** Short label shown in the chat bubble for a clarify selection. */
export function formatClarifySelectionLabel(item: AiHelpClarifyItem): string {
  if (item.kind === 'project') {
    return item.projectName;
  }
  if (item.kind === 'task') {
    return item.taskName;
  }
  return item.name;
}

function toGuideHistoryPayload(content: AiHelpGuideContent): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    reply_type: 'guide',
    intro: content.intro,
    methods: content.methods.map((method) => {
      const entry: Record<string, unknown> = {
        label: method.label,
        steps: method.steps.map((step) => {
          const stepEntry: Record<string, unknown> = { text: step.text };
          if (step.link) {
            stepEntry['link'] = { label: step.link.label, url: step.link.url };
          }
          return stepEntry;
        })
      };
      if (method.alternative != null) {
        entry['alternative'] = method.alternative;
      }
      if (method.video) {
        entry['video'] = method.video;
      }
      return entry;
    }),
    isAudio: content.isAudio === true
  };

  if (content.matchedId) {
    payload['matched_id'] = content.matchedId;
  }
  if (content.note) {
    payload['note'] = content.note;
  }
  if (content.video) {
    payload['video'] = content.video;
  }

  return payload;
}

export function formatDataLabel(key: string): string {
  return key
    .trim()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function formatDataValue(value: AiHelpDataValue): string {
  if (value === null) {
    return '—';
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  return String(value);
}

export function formatDaysOverdue(value: number): string {
  if (!Number.isFinite(value)) {
    return '';
  }
  const days = Math.round(value);
  return days === 1 ? '1 day overdue' : `${days} days overdue`;
}

export function formatOverdueCount(value: number): string {
  if (!Number.isFinite(value)) {
    return '';
  }
  const count = Math.round(value);
  return count === 1 ? '1 overdue task' : `${count} overdue tasks`;
}

export function formatDaysRemaining(value: number): string {
  if (!Number.isFinite(value)) {
    return '';
  }
  const days = Math.round(value);
  if (days > 0) {
    return days === 1 ? '1 day remaining' : `${days} days remaining`;
  }
  if (days === 0) {
    return 'Due today';
  }
  const overdue = Math.abs(days);
  return overdue === 1 ? '1 day overdue' : `${overdue} days overdue`;
}

/** Format a generic data-record value using the field name (hours, dates, percents). */
export function formatRecordFieldValue(key: string, value: AiHelpDataValue): string {
  const normalized = key.trim().toLowerCase();
  if (typeof value === 'number' && Number.isFinite(value)) {
    if (normalized.includes('percent')) {
      return formatPercentComplete(value);
    }
    if (normalized.includes('hour')) {
      return formatHours(value);
    }
    if (normalized.includes('days_remaining') || normalized.includes('days remaining')) {
      return formatDaysRemaining(value);
    }
  }
  if (typeof value === 'string' && value.trim() && (normalized.endsWith('date') || normalized.includes('_date'))) {
    return formatDailyHoursDate(value);
  }
  return formatDataValue(value);
}

export function formatPercentComplete(value: number): string {
  if (!Number.isFinite(value)) {
    return '—';
  }
  const rounded = Math.round(value * 100) / 100;
  return Number.isInteger(rounded) ? `${rounded}%` : `${rounded.toFixed(2)}%`;
}

export function formatHours(value: number): string {
  if (!Number.isFinite(value)) {
    return '—';
  }
  const rounded = Math.round(value * 100) / 100;
  return Number.isInteger(rounded) ? `${rounded}h` : `${rounded}h`;
}

/** Compact number for `stat_cards` values (`1429` → `1,429`). */
export function formatDataBlockStatValue(value: string | number): string {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  const trimmed = String(value).trim();
  return trimmed || '—';
}

/** Format `YYYY-MM-DD` (or parseable date) as `July 13, 2026`. */
export function formatDailyHoursDate(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) {
    return value;
  }

  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (isoMatch) {
    const year = Number(isoMatch[1]);
    const month = Number(isoMatch[2]);
    const day = Number(isoMatch[3]);
    if (
      Number.isInteger(year) &&
      Number.isInteger(month) &&
      Number.isInteger(day) &&
      month >= 1 &&
      month <= 12 &&
      day >= 1 &&
      day <= 31
    ) {
      const date = new Date(year, month - 1, day);
      if (
        date.getFullYear() === year &&
        date.getMonth() === month - 1 &&
        date.getDate() === day
      ) {
        return date.toLocaleDateString('en-US', {
          month: 'long',
          day: 'numeric',
          year: 'numeric'
        });
      }
    }
  }

  const parsed = new Date(trimmed);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  }

  return trimmed;
}

/** Resolve relative helpdesk media paths to absolute URLs. */
export function resolveAiHelpMediaUrl(path: string, baseUrl: string): string {
  const trimmed = path.trim();
  if (!trimmed) {
    return '';
  }

  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }

  const base = baseUrl.replace(/\/$/, '');
  return trimmed.startsWith('/') ? `${base}${trimmed}` : `${base}/${trimmed}`;
}

export function normalizeAiHelpResponse(response: AiHelpAskResponse, mediaBaseUrl: string): AiHelpAskResponse {
  const content = response.content;
  if (content.kind !== 'guide') {
    return response;
  }

  return {
    ...response,
    content: {
      ...content,
      video: content.video ? resolveAiHelpMediaUrl(content.video, mediaBaseUrl) : undefined,
      methods: content.methods.map((method) => ({
        ...method,
        video: method.video ? resolveAiHelpMediaUrl(method.video, mediaBaseUrl) : undefined
      }))
    }
  };
}

function toOffTopicHistoryPayload(content: AiHelpOffTopicContent): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    reply_type: 'off_topic',
    message: content.text,
    text: content.text,
    isAudio: content.isAudio === true
  };

  if (content.action) {
    payload['show_sample_prompts'] = true;
    payload['action'] = {
      type: content.action.type,
      label: content.action.label,
      action: content.action.action
    };
  }

  return payload;
}

function extractOffTopic(raw: unknown): AiHelpOffTopicContent | null {
  for (const obj of collectObjects(raw)) {
    if (obj['reply_type'] !== 'off_topic') {
      continue;
    }

    const text = extractClarifyText(obj);
    if (!text) {
      return null;
    }

    return {
      kind: 'off_topic',
      text,
      action: parseOffTopicAction(obj),
      isAudio: obj['isAudio'] === true
    };
  }

  return null;
}

/** Button from `action`, or a default sample-prompts button when `show_sample_prompts` is set. */
function parseOffTopicAction(obj: Record<string, unknown>): AiHelpReplyAction | undefined {
  const parsed = parseReplyAction(obj['action']);
  if (parsed) {
    return parsed;
  }

  if (obj['show_sample_prompts'] === true) {
    return {
      type: 'button',
      label: 'See sample prompts',
      action: 'open_sample_prompts'
    };
  }

  return undefined;
}

const KNOWN_REPLY_ACTIONS = new Set<AiHelpReplyAction['action']>([
  'open_sample_prompts',
  'download_pdf',
  'send_email',
  'confirm_send_email'
]);

function parseReplyActions(value: unknown): AiHelpReplyAction[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => parseReplyAction(entry))
    .filter((action): action is AiHelpReplyAction => action != null);
}

function parseReplyAction(value: unknown): AiHelpReplyAction | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }

  const action = value as Record<string, unknown>;
  const actionId =
    typeof action['action'] === 'string' ? action['action'].trim() : '';
  if (
    action['type'] !== 'button' ||
    !KNOWN_REPLY_ACTIONS.has(actionId as AiHelpReplyAction['action'])
  ) {
    return undefined;
  }

  const label = typeof action['label'] === 'string' ? action['label'].trim() : '';
  if (!label) {
    return undefined;
  }

  return {
    type: 'button',
    label,
    action: actionId as AiHelpReplyAction['action']
  };
}

function extractAsk(raw: unknown): AiHelpAskContent | null {
  for (const obj of collectObjects(raw)) {
    if (obj['reply_type'] !== 'ask') {
      continue;
    }

    const text = extractClarifyText(obj);
    if (!text) {
      return null;
    }

    return {
      kind: 'ask',
      text,
      inputType: inferAskInputType(obj, text),
      dateRange: extractAskDateRange(obj, text),
      isAudio: obj['isAudio'] === true
    };
  }

  return null;
}

function extractAskDateRange(
  obj: Record<string, unknown>,
  text: string
): { from: string; to: string } | undefined {
  const structuredCandidates = [
    obj['date_range'],
    obj['dateRange'],
    obj['valid_range'],
    obj['validRange']
  ];

  for (const candidate of structuredCandidates) {
    const range = parseAskDateRangeRecord(candidate);
    if (range) {
      return range;
    }
  }

  const minDate = readIsoDate(obj['min_date'] ?? obj['minDate'] ?? obj['date_from'] ?? obj['dateFrom']);
  const maxDate = readIsoDate(obj['max_date'] ?? obj['maxDate'] ?? obj['date_to'] ?? obj['dateTo']);
  if (minDate && maxDate) {
    return normalizeAskDateRange(minDate, maxDate);
  }

  return parseAskDateRangeFromText(text);
}

function parseAskDateRangeRecord(value: unknown): { from: string; to: string } | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }

  const record = value as Record<string, unknown>;
  const from = readIsoDate(record['from'] ?? record['start'] ?? record['min']);
  const to = readIsoDate(record['to'] ?? record['end'] ?? record['max']);
  if (!from || !to) {
    return undefined;
  }

  return normalizeAskDateRange(from, to);
}

function parseAskDateRangeFromText(text: string): { from: string; to: string } | undefined {
  const fromToMatch = text.match(/from\s+(\d{4}-\d{2}-\d{2})\s+to\s+(\d{4}-\d{2}-\d{2})/i);
  if (fromToMatch) {
    return normalizeAskDateRange(fromToMatch[1], fromToMatch[2]);
  }

  const betweenMatch = text.match(/between\s+(\d{4}-\d{2}-\d{2})\s+and\s+(\d{4}-\d{2}-\d{2})/i);
  if (betweenMatch) {
    return normalizeAskDateRange(betweenMatch[1], betweenMatch[2]);
  }

  const dates = text.match(/\d{4}-\d{2}-\d{2}/g);
  if (dates && dates.length >= 2) {
    return normalizeAskDateRange(dates[0], dates[1]);
  }

  return undefined;
}

function readIsoDate(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  const match = value.trim().match(/^(\d{4}-\d{2}-\d{2})/);
  return match?.[1];
}

function normalizeAskDateRange(from: string, to: string): { from: string; to: string } {
  return from <= to ? { from, to } : { from: to, to: from };
}

function inferAskInputType(obj: Record<string, unknown>, text: string): AiHelpAskInputType {
  const candidates = [
    obj['input'],
    obj['input_type'],
    obj['field_type'],
    obj['field'],
    obj['format'],
    obj['ask_type']
  ];

  for (const candidate of candidates) {
    if (typeof candidate !== 'string') {
      continue;
    }
    const normalized = candidate.toLowerCase().replace(/[_\s]+/g, '-');
    if (
      normalized === 'text' ||
      normalized === 'string' ||
      normalized === 'free-text' ||
      normalized === 'freetext'
    ) {
      return 'text';
    }
    if (normalized.includes('date') || normalized.includes('yyyy-mm-dd')) {
      return 'date';
    }
  }

  // Structured range from the API means the user must pick a date.
  if (extractAskDateRange(obj, '')) {
    return 'date';
  }

  const haystack = text.toLowerCase();

  // Only treat as date when the assistant is clearly requesting a date value —
  // not when "due date" / "start date" is merely mentioned in the question.
  if (
    /yyyy[-/.]mm[-/.]dd/.test(haystack) ||
    /\b(what|which)\s+(?:start |end |due |completion |planned )?dates?\b/.test(haystack) ||
    /\b(enter|provide|pick|select|choose|set|change(?:\s+it)?\s+to)\s+(?:a |the |new )?(?:start |end |due |completion |planned )?dates?\b/.test(
      haystack
    ) ||
    /\b(?:new |updated )?(?:start |end |due |completion |planned )?dates?\s+(?:do you|would you|should|to use|to set|to change)\b/.test(
      haystack
    ) ||
    /\bdates?\s+should\b/.test(haystack)
  ) {
    return 'date';
  }

  return 'text';
}

function extractConfirm(raw: unknown): AiHelpConfirmContent | null {
  for (const obj of collectObjects(raw)) {
    if (obj['reply_type'] !== 'confirm') {
      continue;
    }

    const text = extractClarifyText(obj);
    if (!text) {
      return null;
    }

    const summaryRaw = obj['summary'];
    if (!summaryRaw || typeof summaryRaw !== 'object' || Array.isArray(summaryRaw)) {
      return null;
    }

    const summary = sanitizeConfirmSummaryObject(summaryRaw as Record<string, unknown>);

    if (Object.keys(summary).length === 0) {
      return null;
    }

    return {
      kind: 'confirm',
      text,
      summary,
      isAudio: obj['isAudio'] === true
    };
  }

  return null;
}

function sanitizeConfirmSummaryObject(value: Record<string, unknown>): AiHelpConfirmSummaryObject {
  const summary: AiHelpConfirmSummaryObject = {};

  Object.entries(value).forEach(([key, entry]) => {
    const sanitized = sanitizeConfirmSummaryValue(entry);
    if (sanitized !== undefined) {
      summary[key] = sanitized;
    }
  });

  return summary;
}

function sanitizeConfirmSummaryValue(value: unknown): AiHelpConfirmSummaryValue | undefined {
  if (
    value === null ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return value;
  }

  if (Array.isArray(value)) {
    const sanitizedItems = value
      .map((entry) => sanitizeConfirmSummaryValue(entry))
      .filter((entry): entry is AiHelpConfirmSummaryValue => entry !== undefined);
    return sanitizedItems.length > 0 ? sanitizedItems : undefined;
  }

  if (typeof value === 'object') {
    const sanitizedObject = sanitizeConfirmSummaryObject(value as Record<string, unknown>);
    return Object.keys(sanitizedObject).length > 0 ? sanitizedObject : undefined;
  }

  return undefined;
}

function extractClarify(raw: unknown): AiHelpClarifyContent | null {
  for (const obj of collectObjects(raw)) {
    if (obj['reply_type'] !== 'clarify') {
      continue;
    }

    const text = extractClarifyText(obj);
    if (!text) {
      return null;
    }

    const parsedOptions = parseClarifyOptions(obj['options']);
    const note =
      typeof obj['note'] === 'string' && obj['note'].trim() ? obj['note'].trim() : undefined;

    return {
      kind: 'clarify',
      text,
      options: parsedOptions.groups,
      items: parsedOptions.items,
      choices: parsedOptions.choices,
      note,
      isAudio: obj['isAudio'] === true
    };
  }

  return null;
}

/**
 * Supports option shapes:
 * 1. Grouped: `[{ group: 'Projects', items: [...] }]`
 * 2. Flat: `[{ project_id, project_name }, { task_id|item_id|obj_id, task_name|name, project?, planned_completion? }, { user_id, name, job }, { template_id, name }, { approval_id, name }, { service_id, name }, { service_category_id, name }]`
 *    - Also supports legacy people shape: `[{ id, name, job? }]` (where `id` acts as `user_id`)
 *    - IDs may be string or number
 * 3. String choices: `["Team A", "Team B"]`
 */
function parseClarifyOptions(value: unknown): {
  groups?: AiHelpClarifyOptionGroup[];
  items?: AiHelpClarifyItem[];
  choices?: string[];
} {
  if (!Array.isArray(value) || value.length === 0) {
    return {};
  }

  const choices = parseClarifyChoices(value);
  if (choices.length > 0) {
    return { choices };
  }

  const groups = value
    .map((entry) => parseClarifyOptionGroup(entry))
    .filter((group): group is AiHelpClarifyOptionGroup => group !== null);

  if (groups.length > 0) {
    return { groups };
  }

  const items = parseClarifyItems(value);
  return items.length > 0 ? { items } : {};
}

function parseClarifyChoices(value: unknown[]): string[] {
  if (!value.every((entry) => typeof entry === 'string')) {
    return [];
  }

  return value
    .map((entry) => (entry as string).trim())
    .filter((entry) => entry.length > 0);
}

function parseClarifyOptionGroup(value: unknown): AiHelpClarifyOptionGroup | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const obj = value as Record<string, unknown>;
  const group = typeof obj['group'] === 'string' ? obj['group'].trim() : '';
  if (!group) {
    return null;
  }

  const items = parseClarifyItems(obj['items']);
  if (items.length === 0) {
    return null;
  }

  return { group, items };
}

function parseClarifyItems(value: unknown): AiHelpClarifyItem[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => parseClarifyItem(entry))
    .filter((item): item is AiHelpClarifyItem => item !== null);
}

function parseClarifyItem(value: unknown): AiHelpClarifyItem | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const obj = value as Record<string, unknown>;

  // Project-like clarify options can arrive with legacy keys
  // (`project_id`/`project_name`) or client/portfolio keys
  // (`portfolio_id`/`name`).
  const legacyProjectId = readId(obj['project_id']);
  const portfolioId = readId(obj['portfolio_id']);
  const projectId = legacyProjectId || portfolioId;
  const projectName = readString(obj['project_name']) || readString(obj['name']);
  if (projectId && projectName) {
    return {
      kind: 'project',
      projectId,
      projectName,
      status: readOptionalString(obj['status']),
      owner: readOptionalString(obj['owner']),
      idKey: portfolioId && !legacyProjectId ? 'portfolio_id' : undefined
    };
  }

  const legacyTaskId = readId(obj['task_id']);
  const itemId = readId(obj['item_id']);
  const objId = readId(obj['obj_id']);
  const taskId = legacyTaskId || itemId || objId;
  // Some backends send `name` instead of `task_name`, and `item_id`/`obj_id` instead of `task_id`.
  const taskName = readString(obj['task_name']) || readString(obj['name']);
  if (taskId && taskName) {
    return {
      kind: 'task',
      taskId,
      taskName,
      project: readOptionalString(obj['project']),
      refNo: readRefNo(obj['ref_no']),
      due: readOptionalString(obj['due']),
      plannedCompletion: readOptionalString(obj['planned_completion']),
      idKey: !legacyTaskId ? (itemId ? 'item_id' : objId ? 'obj_id' : undefined) : undefined
    };
  }

  const serviceId = readId(obj['service_id']);
  const serviceName = readString(obj['name']) || readString(obj['service_name']);
  if (serviceId && serviceName) {
    return {
      kind: 'service',
      serviceId,
      name: serviceName
    };
  }

  const serviceCategoryId = readId(obj['service_category_id']);
  const serviceCategoryName =
    readString(obj['name']) || readString(obj['service_category_name']);
  if (serviceCategoryId && serviceCategoryName) {
    return {
      kind: 'service_category',
      serviceCategoryId,
      name: serviceCategoryName
    };
  }

  // Backward-compat: some backends return `{ id, name }` for person options.
  // Treat `id` as `user_id` when `user_id` is missing.
  const userId = readId(obj['user_id']) || readId(obj['id']);
  const name = readString(obj['name']);
  if (userId && name) {
    return {
      kind: 'person',
      userId,
      name,
      job: readOptionalString(obj['job'])
    };
  }

  const templateId = readId(obj['template_id']);
  const templateName = readString(obj['name']);
  if (templateId && templateName) {
    return {
      kind: 'template',
      templateId,
      name: templateName
    };
  }

  const approvalId = readId(obj['approval_id']);
  const approvalName = readString(obj['name']);
  if (approvalId && approvalName) {
    return {
      kind: 'approval',
      approvalId,
      name: approvalName
    };
  }

  return null;
}

/** Coerce string or finite number IDs from clarify option payloads. */
function readId(value: unknown): string {
  if (typeof value === 'string') {
    return value.trim();
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return '';
}

function readString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function readOptionalString(value: unknown): string | undefined {
  const text = readString(value);
  return text || undefined;
}

function readRefNo(value: unknown): number | string | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim()) {
    return value.trim();
  }
  return undefined;
}

const PROJECT_LIST_DATA_KINDS = new Set([
  'project_list',
  'client_projects',
  'delayed_projects',
  'projects_over_90',
  'high_complete_overdue'
]);

function isProjectListDataKind(kind: string): boolean {
  return PROJECT_LIST_DATA_KINDS.has(kind);
}

/** Prefer `{ reply_type: 'data', data }` and also accept a few known bare / flat payloads. */
function resolveDataPayload(
  obj: Record<string, unknown>,
  isDataReply: boolean,
  wrappedData: unknown
): Record<string, unknown> | null {
  if (isDataReply) {
    if (wrappedData && typeof wrappedData === 'object' && !Array.isArray(wrappedData)) {
      return wrappedData as Record<string, unknown>;
    }
    // Flat shape: `{ reply_type: 'data', kind, projects, intro }` (no nested `data`).
    if (isFlatDataReply(obj)) {
      return obj;
    }
    return null;
  }

  const kind = readString(obj['kind']).toLowerCase();
  if (isProjectListDataKind(kind) && (Array.isArray(obj['projects']) || Array.isArray(obj['matches']))) {
    return obj;
  }

  if (kind === 'my_approvals' && Array.isArray(obj['approvals'])) {
    return obj;
  }

  if (kind === 'client_requests' && Array.isArray(obj['requests'])) {
    return obj;
  }

  if (kind === 'overdue_reqs_issues' && Array.isArray(obj['items'])) {
    return obj;
  }

  if (
    kind === 'work_today' &&
    (Array.isArray(obj['do_now']) || Array.isArray(obj['coming_up']) || obj['has_work'] != null)
  ) {
    return obj;
  }

  if ((kind === 'team_overdue' || kind === 'my_overdue') && Array.isArray(obj['tasks'])) {
    return obj;
  }

  if (kind === 'my_pending_service_tasks' && Array.isArray(obj['tasks'])) {
    return obj;
  }

  if (kind === 'free_hours' && Array.isArray(obj['people'])) {
    return obj;
  }

  return null;
}

/** True when project-list (or similar) fields live on the reply root instead of under `data`. */
function isFlatDataReply(obj: Record<string, unknown>): boolean {
  const kind = readString(obj['kind']).toLowerCase();
  if (isProjectListDataKind(kind)) {
    return true;
  }
  if (kind === 'free_hours' && Array.isArray(obj['people'])) {
    return true;
  }
  if (kind === 'email_preview' && isPlainObject(obj['email'])) {
    return true;
  }
  return (
    Array.isArray(obj['blocks']) ||
    Array.isArray(obj['projects']) ||
    Array.isArray(obj['matches']) ||
    Array.isArray(obj['tasks']) ||
    Array.isArray(obj['items']) ||
    Array.isArray(obj['approvals']) ||
    Array.isArray(obj['requests'])
  );
}

function extractData(raw: unknown): AiHelpDataContent | null {
  for (const obj of collectObjects(raw)) {
    const wrappedData = obj['data'];
    const isDataReply = obj['reply_type'] === 'data';
    const nestedDataObj =
      wrappedData && typeof wrappedData === 'object' && !Array.isArray(wrappedData)
        ? (wrappedData as Record<string, unknown>)
        : null;
    const nestedBlocks = nestedDataObj ? parseDataBlocks(nestedDataObj['blocks']) : [];
    const rootBlocks = parseDataBlocks(obj['blocks']);
    const blocks = nestedBlocks.length ? nestedBlocks : rootBlocks;
    const actions = parseReplyActions(obj['actions']);

    const dataObj = resolveDataPayload(obj, isDataReply, wrappedData);
    if (!dataObj) {
      if (isDataReply && blocks.length) {
        const intro = typeof obj['intro'] === 'string' ? obj['intro'].trim() : '';
        return {
          kind: 'data',
          intro,
          isAudio: obj['isAudio'] === true,
          apiData: nestedDataObj ?? obj,
          dataKind: 'blocks',
          data: {},
          blocks,
          ...(actions.length ? { actions } : {})
        };
      }
      continue;
    }

    const dataKindRaw = readString(dataObj['kind']).toLowerCase();
    const intro = typeof obj['intro'] === 'string' ? obj['intro'].trim() : '';
    const isAudio = obj['isAudio'] === true;

    const toContent = (
      fields: Omit<AiHelpDataContent, 'kind' | 'intro' | 'isAudio' | 'apiData' | 'actions'>
    ): AiHelpDataContent => ({
      kind: 'data',
      intro,
      isAudio,
      apiData: dataObj,
      ...(actions.length ? { actions } : {}),
      ...fields
    });

    if (dataKindRaw === 'email_preview') {
      const emailPreviewContent = parseDataEmailPreview(dataObj['email']);
      if (!emailPreviewContent) {
        return null;
      }

      return toContent({
        dataKind: 'email_preview',
        data: {},
        emailPreviewContent
      });
    }

    if (dataKindRaw === 'projects_over_estimate') {
      const projectsOverEstimateContent = parseDataProjectsOverEstimate(dataObj);
      if (!projectsOverEstimateContent) {
        return null;
      }

      return toContent({
        dataKind: 'projects_over_estimate',
        data: {},
        projectsOverEstimateContent
      });
    }

    if (isProjectListDataKind(dataKindRaw)) {
      // Empty lists are valid — API often returns intro-only “no matches” replies.
      const projects = parseDataProjects(dataObj['projects'] ?? dataObj['matches']);
      if (projects.length) {
        return toContent({
          dataKind: 'project_list',
          data: {},
          projects
        });
      }
      if (blocks.length) {
        return toContent({
          dataKind: 'blocks',
          data: parseDataRecord(dataObj),
          blocks
        });
      }

      return toContent({
        dataKind: 'project_list',
        data: parseDataRecord(dataObj),
        projects
      });
    }

    if (dataKindRaw === 'project_tasks') {
      const projectTasksContent = parseDataProjectTasks(dataObj);
      if (!projectTasksContent) {
        return null;
      }

      return toContent({
        dataKind: 'project_tasks',
        data: {},
        projectTasksContent
      });
    }

    if (dataKindRaw === 'project_task_breakdown') {
      const projectTaskBreakdownContent = parseDataProjectTaskBreakdown(dataObj);
      if (!projectTaskBreakdownContent) {
        return null;
      }

      return toContent({
        dataKind: 'project_task_breakdown',
        data: {},
        projectTaskBreakdownContent
      });
    }

    if (
      dataKindRaw === 'overdue_tasks' ||
      dataKindRaw === 'team_overdue' ||
      dataKindRaw === 'my_overdue'
    ) {
      const overdueTasksContent = parseDataOverdueTasks(dataObj);
      if (!overdueTasksContent) {
        return null;
      }

      return toContent({
        dataKind: 'overdue_tasks',
        data: {},
        overdueTasksContent
      });
    }

    if (dataKindRaw === 'overdue_reqs_issues') {
      const overdueReqsIssuesContent = parseDataOverdueReqsIssues(dataObj);
      if (!overdueReqsIssuesContent) {
        return null;
      }

      return toContent({
        dataKind: 'overdue_reqs_issues',
        data: {},
        overdueReqsIssuesContent
      });
    }

    if (dataKindRaw === 'priority_tasks') {
      const priorityTasksContent = parseDataPriorityTasks(dataObj);
      if (!priorityTasksContent) {
        return null;
      }

      return toContent({
        dataKind: 'priority_tasks',
        data: {},
        priorityTasksContent
      });
    }

    if (dataKindRaw === 'my_pending_service_tasks') {
      const pendingServiceTasksContent = parseDataPendingServiceTasks(dataObj);
      if (!pendingServiceTasksContent) {
        return null;
      }

      return toContent({
        dataKind: 'my_pending_service_tasks',
        data: {},
        pendingServiceTasksContent
      });
    }

    if (dataKindRaw === 'items_due') {
      const itemsDueContent = parseDataItemsDue(dataObj);
      if (!itemsDueContent) {
        return null;
      }

      return toContent({
        dataKind: 'items_due',
        data: {},
        itemsDueContent
      });
    }

    if (dataKindRaw === 'work_today') {
      return toContent({
        dataKind: 'work_today',
        data: {},
        workTodayContent: parseDataWorkToday(dataObj)
      });
    }

    if (dataKindRaw === 'completed_items') {
      const completedItemsContent = parseDataCompletedItems(dataObj);
      if (!completedItemsContent) {
        return null;
      }

      return toContent({
        dataKind: 'completed_items',
        data: {},
        completedItemsContent
      });
    }

    if (dataKindRaw === 'current_work') {
      return toContent({
        dataKind: 'current_work',
        data: {},
        currentWorkContent: parseDataCurrentWork(dataObj)
      });
    }

    if (dataKindRaw === 'project_health') {
      const projectHealthContent = parseDataProjectHealth(dataObj);
      if (!projectHealthContent) {
        return null;
      }

      return toContent({
        dataKind: 'project_health',
        data: {},
        projectHealthContent
      });
    }

    if (dataKindRaw === 'updates') {
      const updatesContent = parseDataUpdates(dataObj);
      if (!updatesContent) {
        return null;
      }

      return toContent({
        dataKind: 'updates',
        data: {},
        updatesContent
      });
    }

    if (dataKindRaw === 'comment_search') {
      const commentSearchContent = parseDataCommentSearch(dataObj);
      if (!commentSearchContent) {
        return null;
      }

      return toContent({
        dataKind: 'comment_search',
        data: {},
        commentSearchContent
      });
    }

    if (dataKindRaw === 'my_approvals') {
      const myApprovalsContent = parseDataMyApprovals(dataObj);
      if (!myApprovalsContent) {
        return null;
      }

      return toContent({
        dataKind: 'my_approvals',
        data: {},
        myApprovalsContent
      });
    }

    if (dataKindRaw === 'client_requests') {
      const clientRequestsContent = parseDataClientRequests(dataObj);
      if (!clientRequestsContent) {
        return null;
      }

      return toContent({
        dataKind: 'client_requests',
        data: {},
        clientRequestsContent
      });
    }

    if (
      dataKindRaw === 'ranking' ||
      dataKindRaw === 'ai_hours_ranking' ||
      dataKindRaw === 'task_load_ranking'
    ) {
      const rankingContent = parseDataRanking(dataObj, {
        isAiHoursRanking: dataKindRaw === 'ai_hours_ranking',
        isTaskLoadRanking: dataKindRaw === 'task_load_ranking'
      });
      if (!rankingContent) {
        return null;
      }

      return toContent({
        dataKind: 'ranking',
        data: {},
        rankingContent
      });
    }

    if (dataKindRaw === 'user') {
      const userContent = parseDataUser(dataObj);
      if (!userContent) {
        return null;
      }

      return toContent({
        dataKind: 'user',
        data: {},
        userContent
      });
    }

    if (dataKindRaw === 'daily_hours') {
      const dailyHoursContent = parseDataDailyHours(dataObj);
      if (!dailyHoursContent) {
        return null;
      }

      return toContent({
        dataKind: 'daily_hours',
        data: {},
        dailyHoursContent
      });
    }

    if (dataKindRaw === 'ai_hours_trend') {
      const aiHoursTrendContent = parseDataAiHoursTrend(dataObj);
      if (!aiHoursTrendContent) {
        return null;
      }

      return toContent({
        dataKind: 'ai_hours_trend',
        data: {},
        aiHoursTrendContent
      });
    }

    if (dataKindRaw === 'leave') {
      const leaveContent = parseDataLeave(dataObj);
      if (!leaveContent) {
        return null;
      }

      return toContent({
        dataKind: 'leave',
        data: {},
        leaveContent
      });
    }

    if (dataKindRaw === 'free_hours') {
      const freeHoursContent = parseDataFreeHours(dataObj);
      if (!freeHoursContent) {
        return null;
      }

      return toContent({
        dataKind: 'free_hours',
        data: {},
        freeHoursContent
      });
    }

    if (dataKindRaw === 'timesheet_submission') {
      const timesheetSubmissionContent = parseDataTimesheetSubmission(dataObj);
      if (!timesheetSubmissionContent) {
        return null;
      }

      return toContent({
        dataKind: 'timesheet_submission',
        data: {},
        timesheetSubmissionContent
      });
    }

    if (dataKindRaw === 'client_brief') {
      const clientBriefContent = parseDataClientBrief(dataObj);
      if (!clientBriefContent) {
        return null;
      }

      return toContent({
        dataKind: 'client_brief',
        data: {},
        clientBriefContent
      });
    }

    const projects = parseDataProjects(dataObj['projects']);
    if (projects.length > 0) {
      return toContent({
        dataKind: 'project_list',
        data: {},
        projects
      });
    }

    if (blocks.length) {
      return toContent({
        dataKind: 'blocks',
        data: {},
        blocks
      });
    }

    const data = parseDataRecord(dataObj);
    if (Object.keys(data).length === 0) {
      return null;
    }

    return toContent({
      dataKind: 'record',
      data
    });
  }

  return null;
}

function parseDataEmailPreview(value: unknown): AiHelpDataEmailPreviewContent | null {
  if (!isPlainObject(value)) {
    return null;
  }

  const email = value as Record<string, unknown>;
  const content = readString(email['content']);
  const previewHtml = readString(email['preview_html']) || content;
  const to = parseEmailAddresses(email['to']);
  if (!to.length && !previewHtml) {
    return null;
  }

  return {
    to,
    cc: parseEmailAddresses(email['cc']),
    subject: readString(email['subject']),
    content,
    previewHtml,
    raw: email
  };
}

function parseEmailAddresses(value: unknown): string[] {
  const list = Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? value.split(/[,;]/)
      : [];
  return list
    .map((entry) => (typeof entry === 'string' ? entry.trim() : ''))
    .filter(Boolean);
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function parseDataBlocks(value: unknown): AiHelpDataBlock[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => parseDataBlock(entry))
    .filter((block): block is AiHelpDataBlock => block !== null);
}

function parseDataBlock(value: unknown): AiHelpDataBlock | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  const obj = value as Record<string, unknown>;
  const type = readString(obj['type']).toLowerCase();

  if (type === 'stat_cards') {
    const items = parseDataStatCardItems(obj['items']);
    if (!items.length) {
      return null;
    }

    const block: AiHelpDataStatCardsBlock = { type: 'stat_cards', items };
    const title = readOptionalString(obj['title']);
    if (title) {
      block.title = title;
    }
    return block;
  }

  if (type === 'item_list') {
    const items = parseDataItemListItems(obj['items']);
    if (!items.length) {
      return null;
    }

    const block: AiHelpDataItemListBlock = { type: 'item_list', items };
    const title = readOptionalString(obj['title']);
    if (title) {
      block.title = title;
    }
    return block;
  }

  if (type === 'ranking') {
    const items = parseDataRankingBlockItems(obj['items'] ?? obj['ranking']);
    if (!items.length) {
      return null;
    }

    const block: AiHelpDataRankingBlock = { type: 'ranking', items };
    const title = readOptionalString(obj['title']);
    if (title) {
      block.title = title;
    }
    return block;
  }

  if (type === 'key_values' || type === 'key_value' || type === 'kv') {
    const rows = parseDataKeyValueRows(obj['rows'] ?? obj['items']);
    if (!rows.length) {
      return null;
    }

    const block: AiHelpDataKeyValuesBlock = { type: 'key_values', rows };
    const title = readOptionalString(obj['title']);
    if (title) {
      block.title = title;
    }
    return block;
  }

  if (type === 'table') {
    const columns = parseDataTableColumns(obj['list'] ?? obj['columns'] ?? obj['headers']);
    const rows = parseDataTableRows(obj['row'] ?? obj['rows'] ?? obj['items'], columns);
    if (!columns.length || !rows.length) {
      return null;
    }

    const block: AiHelpDataTableBlock = { type: 'table', columns, rows };
    const title = readOptionalString(obj['title']) || readOptionalString(obj['heading']);
    if (title) {
      block.title = title;
    }
    return block;
  }

  if (type === 'section') {
    const text = readString(obj['text']) || readString(obj['content']);
    if (!text) {
      return null;
    }

    const block: AiHelpDataSectionBlock = {
      type: 'section',
      text,
      segments: parseDataTextSegments(text)
    };
    const heading = readOptionalString(obj['heading']) || readOptionalString(obj['title']);
    if (heading) {
      block.heading = heading;
    }
    return block;
  }

  const text = readString(obj['text']) || readString(obj['content']);
  if (type === 'text' || type === 'markdown' || text) {
    if (!text) {
      return null;
    }

    const block: AiHelpDataTextBlock = {
      type: 'text',
      text,
      segments: parseDataTextSegments(text)
    };
    return block;
  }

  return null;
}

/** Column defs from a `table` block (`list` / `columns` / `headers`). */
function parseDataTableColumns(value: unknown): AiHelpDataTableColumn[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => parseDataTableColumn(entry))
    .filter((col): col is AiHelpDataTableColumn => col !== null);
}

function parseDataTableColumn(value: unknown): AiHelpDataTableColumn | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  const obj = value as Record<string, unknown>;
  const prop =
    readString(obj['prop']) ||
    readString(obj['key']) ||
    readString(obj['field']) ||
    readString(obj['name']);
  const fieldName =
    readString(obj['fieldName']) ||
    readString(obj['field_name']) ||
    readString(obj['label']) ||
    readString(obj['title']) ||
    prop;
  if (!prop || !fieldName) {
    return null;
  }

  return { fieldName, prop };
}

/** Data rows from a `table` block; keeps only string/number/boolean cell values. */
function parseDataTableRows(
  value: unknown,
  columns: AiHelpDataTableColumn[]
): Record<string, string>[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => parseDataTableRow(entry, columns))
    .filter((row): row is Record<string, string> => row !== null);
}

function parseDataTableRow(
  value: unknown,
  columns: AiHelpDataTableColumn[]
): Record<string, string> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  const obj = value as Record<string, unknown>;
  const row: Record<string, string> = {};
  let hasValue = false;

  if (columns.length) {
    for (const col of columns) {
      const cell = formatTableCellValue(obj[col.prop]);
      if (cell) {
        row[col.prop] = cell;
        hasValue = true;
      }
    }
  } else {
    for (const [key, raw] of Object.entries(obj)) {
      const cell = formatTableCellValue(raw);
      if (cell) {
        row[key] = cell;
        hasValue = true;
      }
    }
  }

  return hasValue ? row : null;
}

function formatTableCellValue(value: unknown): string {
  if (value == null) {
    return '';
  }
  if (typeof value === 'string') {
    return value.trim();
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return '';
}

/** Ranking rows inside a `blocks` entry (`items` may use hours, open_items, or by_task). */
function parseDataRankingBlockItems(value: unknown): AiHelpDataRankingItem[] {
  if (!Array.isArray(value)) {
    return [];
  }

  const items = value
    .map((entry) => parseDataRankingBlockItem(entry))
    .filter((item): item is AiHelpDataRankingItem => item !== null);

  return withDerivedRankingPercents(items);
}

/** Fill missing `percent` from hours, open_items, or days share of the list total. */
function withDerivedRankingPercents(items: AiHelpDataRankingItem[]): AiHelpDataRankingItem[] {
  if (!items.length) {
    return items;
  }

  const totalHours = items.reduce((sum, item) => sum + (item.hours ?? 0), 0);
  const totalOpen = items.reduce((sum, item) => sum + (item.openItems ?? 0), 0);
  const totalDays = items.reduce((sum, item) => sum + (item.days ?? 0), 0);

  return items.map((item) => {
    if (item.percent != null) {
      return item;
    }
    if (item.days != null && totalDays > 0) {
      return {
        ...item,
        percent: Math.max(0, Math.min(100, (item.days / totalDays) * 100))
      };
    }
    if (item.hours != null && totalHours > 0) {
      return {
        ...item,
        percent: Math.max(0, Math.min(100, (item.hours / totalHours) * 100))
      };
    }
    if (item.openItems != null && totalOpen > 0) {
      return {
        ...item,
        percent: Math.max(0, Math.min(100, (item.openItems / totalOpen) * 100))
      };
    }
    return item;
  });
}

function parseDataRankingBlockItem(value: unknown): AiHelpDataRankingItem | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const obj = value as Record<string, unknown>;
  // People/teams use `name`/`user`/`team`; task/item breakdowns use `item`;
  // category rankings (e.g. leave `{ type, days }`) only carry `type`, which then becomes the name.
  const rawType = readOptionalString(obj['type']);
  const ownName =
    readString(obj['name']) ||
    readString(obj['user']) ||
    readString(obj['team']) ||
    readString(obj['item']);
  const name = ownName || rawType || '';
  if (!name) {
    return null;
  }

  const percent = readRankingPercent(obj['percent']);
  const byTask = parseDataUserTaskItems(obj['by_task']);
  const itemType = ownName ? rawType : undefined;
  const openItems = readOptionalNumber(obj['open_items']);
  if (openItems != null) {
    return {
      name,
      openItems,
      percent,
      ...(itemType ? { type: itemType } : {}),
      ...(byTask.length ? { byTask } : {})
    };
  }

  const days = readOptionalNumber(obj['days']);
  if (days != null) {
    return {
      name,
      days,
      percent,
      ...(itemType ? { type: itemType } : {})
    };
  }

  const hours =
    readOptionalNumber(obj['hours']) ??
    readOptionalNumber(obj['total_hours']) ??
    readOptionalNumber(obj['ai_hours']);

  // Hours ranking, or by_task-only rows (derive total from tasks).
  if (hours != null) {
    return {
      name,
      hours,
      percent,
      ...(itemType ? { type: itemType } : {}),
      ...(byTask.length ? { byTask } : {})
    };
  }

  if (byTask.length) {
    const derivedHours = byTask.reduce((sum, task) => sum + task.hours, 0);
    return {
      name,
      hours: derivedHours,
      percent,
      ...(itemType ? { type: itemType } : {}),
      byTask
    };
  }

  return null;
}

function parseDataKeyValueRows(value: unknown): AiHelpDataKeyValueRow[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => parseDataKeyValueRow(entry))
    .filter((row): row is AiHelpDataKeyValueRow => row !== null);
}

function parseDataKeyValueRow(value: unknown): AiHelpDataKeyValueRow | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  const obj = value as Record<string, unknown>;
  const label = readString(obj['label']) || readString(obj['key']) || readString(obj['name']);
  const rawValue = obj['value'] ?? obj['val'];
  if (!label || rawValue == null) {
    return null;
  }

  const display =
    typeof rawValue === 'string' || typeof rawValue === 'number' || typeof rawValue === 'boolean'
      ? String(rawValue).trim()
      : '';
  if (!display) {
    return null;
  }

  return { label, value: display };
}

/** Prefer `Item label` from a sibling `key_values` block when formatting ranking values. */
export function resolveBlocksItemLabel(blocks: AiHelpDataBlock[] | undefined): string | undefined {
  if (!blocks?.length) {
    return undefined;
  }

  for (const block of blocks) {
    if (block.type !== 'key_values') {
      continue;
    }
    const row = block.rows.find((entry) => /item\s*label/i.test(entry.label));
    if (row?.value) {
      return row.value;
    }
  }

  return undefined;
}

/** Prefer `Scope` / `Scope label` from a sibling `key_values` block for ranking summary. */
export function resolveBlocksScopeLabel(blocks: AiHelpDataBlock[] | undefined): string | undefined {
  if (!blocks?.length) {
    return undefined;
  }

  for (const block of blocks) {
    if (block.type !== 'key_values') {
      continue;
    }
    const row = block.rows.find((entry) => /^scope(\s*label)?$/i.test(entry.label.trim()));
    if (row?.value) {
      return row.value;
    }
  }

  return undefined;
}

/** Turn API field names like `Scope label` into short UI labels. */
export function formatKeyValueLabel(label: string): string {
  const trimmed = label.trim();
  if (/^scope\s*label$/i.test(trimmed)) {
    return 'Scope';
  }
  if (/^item\s*label$/i.test(trimmed)) {
    return 'Items';
  }
  return trimmed;
}

function parseDataTextSegments(text: string): AiHelpDataTextSegment[] {
  const segments: AiHelpDataTextSegment[] = [];
  let bullets: AiHelpDataTextInlineRun[][] | null = null;
  let numbered: AiHelpDataTextInlineRun[][] | null = null;

  const flushLists = (): void => {
    if (bullets?.length) {
      segments.push({ kind: 'bullets', items: bullets });
    }
    if (numbered?.length) {
      segments.push({ kind: 'numbered', items: numbered });
    }
    bullets = null;
    numbered = null;
  };

  text.replace(/\r\n/g, '\n').split('\n').forEach((rawLine) => {
    const line = rawLine.trim();
    if (!line) {
      flushLists();
      return;
    }

    const heading = /^\*\*(.+)\*\*$/.exec(line);
    if (heading) {
      flushLists();
      segments.push({ kind: 'heading', runs: parseDataTextInlineRuns(heading[1].trim()) });
      return;
    }

    const bullet = /^[-*•]\s+(.+)$/.exec(line);
    if (bullet) {
      if (numbered?.length) {
        segments.push({ kind: 'numbered', items: numbered });
        numbered = null;
      }
      bullets = bullets ?? [];
      bullets.push(parseDataTextInlineRuns(bullet[1].trim()));
      return;
    }

    const numberedMatch = /^\d+[.)]\s+(.+)$/.exec(line);
    if (numberedMatch) {
      if (bullets?.length) {
        segments.push({ kind: 'bullets', items: bullets });
        bullets = null;
      }
      numbered = numbered ?? [];
      numbered.push(parseDataTextInlineRuns(numberedMatch[1].trim()));
      return;
    }

    flushLists();
    segments.push({ kind: 'paragraph', runs: parseDataTextInlineRuns(line) });
  });

  flushLists();
  return segments;
}

function parseDataTextInlineRuns(text: string): AiHelpDataTextInlineRun[] {
  const runs: AiHelpDataTextInlineRun[] = [];
  const boldPattern = /\*\*(.+?)\*\*/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = boldPattern.exec(text))) {
    if (match.index > lastIndex) {
      runs.push({ text: text.slice(lastIndex, match.index) });
    }
    runs.push({ text: match[1], bold: true });
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    runs.push({ text: text.slice(lastIndex) });
  }

  return runs.length ? runs : [{ text }];
}

function stripMarkdownEmphasis(text: string): string {
  return text.replace(/\*\*(.+?)\*\*/g, '$1').trim();
}

function parseDataStatCardItems(value: unknown): AiHelpDataStatCardItem[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => parseDataStatCardItem(entry))
    .filter((item): item is AiHelpDataStatCardItem => item !== null);
}

function parseDataStatCardItem(value: unknown): AiHelpDataStatCardItem | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  const obj = value as Record<string, unknown>;
  const label = readString(obj['label']);
  const parsedValue = readStatCardValue(obj['value']);
  if (!label || parsedValue == null) {
    return null;
  }

  return {
    label,
    value: parsedValue,
    tone: parseStatCardTone(obj['tone'])
  };
}

function parseDataItemListItems(value: unknown): AiHelpDataItemListItem[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => parseDataItemListItem(entry))
    .filter((item): item is AiHelpDataItemListItem => item !== null);
}

function parseDataItemListItem(value: unknown): AiHelpDataItemListItem | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  const obj = value as Record<string, unknown>;
  const title =
    readString(obj['title']) || readString(obj['name']) || readString(obj['label']);
  if (!title) {
    return null;
  }

  const item: AiHelpDataItemListItem = { title };
  const priority = readOptionalString(obj['priority']);
  if (priority) {
    item.priority = priority;
  }

  const status = readOptionalString(obj['status']);
  if (status) {
    item.status = status;
  }

  const assignees = readOptionalString(obj['assignees'] ?? obj['assignee']);
  if (assignees) {
    item.assignees = assignees;
  }

  const link = readOptionalString(obj['link'] ?? obj['url']);
  if (link) {
    item.link = link;
  }

  const originalDate = readOptionalString(obj['original_date'] ?? obj['originalDate']);
  if (originalDate) {
    item.originalDate = originalDate;
  }

  const projectedDate = readOptionalString(
    obj['projected_date'] ?? obj['projectedDate'] ?? obj['actual_date'] ?? obj['actualDate']
  );
  if (projectedDate) {
    item.projectedDate = projectedDate;
  }

  const delayBusinessDays = readOptionalNumber(
    obj['delay_business_days'] ??
      obj['delayBusinessDays'] ??
      obj['delay_days'] ??
      obj['delayDays'] ??
      obj['days_late'] ??
      obj['daysLate']
  );
  if (delayBusinessDays != null) {
    item.delayBusinessDays = delayBusinessDays;
  }

  const totalHours =
    readOptionalNumber(obj['total_hours'] ?? obj['totalHours']) ??
    readOptionalNumber(obj['hours']);
  if (totalHours != null) {
    item.totalHours = totalHours;
  }

  const billableHours = readOptionalNumber(obj['billable_hours'] ?? obj['billableHours']);
  if (billableHours != null) {
    item.billableHours = billableHours;
  }

  const aiHours = readOptionalNumber(obj['ai_hours'] ?? obj['aiHours']);
  if (aiHours != null) {
    item.aiHours = aiHours;
  }

  const submitted = readOptionalBoolean(obj['submitted']);
  if (submitted != null) {
    item.submitted = submitted;
  }

  const overProjects = parseDataItemListOverProjects(
    obj['over_projects'] ?? obj['overProjects'] ?? obj['projects']
  );
  if (overProjects.length) {
    item.overProjects = overProjects;
  }

  const tickets = parseDataItemListTickets(obj['tickets']);
  if (tickets.length) {
    item.tickets = tickets;
  }

  return item;
}

/** Spoken / copy line for a late / delayed `item_list` row. */
export function formatItemListDelayLine(item: AiHelpDataItemListItem): string | null {
  if (item.delayBusinessDays == null || !Number.isFinite(item.delayBusinessDays)) {
    return null;
  }
  const days = Math.round(item.delayBusinessDays);
  const delayLabel =
    days === 1 ? '1 business day late' : `${days} business days late`;

  const original = item.originalDate ? formatDailyHoursDate(item.originalDate) : '';
  const projected = item.projectedDate ? formatDailyHoursDate(item.projectedDate) : '';
  if (original && projected) {
    return `${delayLabel} (${original} → ${projected})`;
  }
  if (original) {
    return `${delayLabel} (original ${original})`;
  }
  if (projected) {
    return `${delayLabel} (projected ${projected})`;
  }
  return delayLabel;
}

/** Spoken / copy line for an `item_list` row (title + priority, delay, hours, or ticket count). */
export function formatItemListItemLine(item: AiHelpDataItemListItem): string {
  const delayLine = formatItemListDelayLine(item);
  if (delayLine) {
    return `${item.title} — ${delayLine}`;
  }

  const hoursParts: string[] = [];
  if (item.totalHours != null) {
    hoursParts.push(formatHours(item.totalHours));
  }
  if (item.billableHours != null) {
    hoursParts.push(`billable ${formatHours(item.billableHours)}`);
  }
  if (item.aiHours != null) {
    hoursParts.push(`AI ${formatHours(item.aiHours)}`);
  }
  if (item.submitted === true) {
    hoursParts.push('submitted');
  } else if (item.submitted === false) {
    hoursParts.push('not submitted');
  }
  if (hoursParts.length) {
    return `${item.title} — ${hoursParts.join(', ')}`;
  }

  if (item.tickets?.length) {
    const count = item.tickets.length;
    return `${item.title} — ${count === 1 ? '1 ticket' : `${count} tickets`}`;
  }

  const detailParts: string[] = [];
  if (item.status) {
    detailParts.push(item.status);
  } else if (item.priority) {
    detailParts.push(item.priority);
  }
  if (item.assignees) {
    detailParts.push(`assignees ${item.assignees}`);
  }
  return detailParts.length ? `${item.title} — ${detailParts.join(', ')}` : item.title;
}

function parseDataItemListTickets(value: unknown): AiHelpDataItemListTicket[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => parseDataItemListTicket(entry))
    .filter((item): item is AiHelpDataItemListTicket => item !== null);
}

function parseDataItemListTicket(value: unknown): AiHelpDataItemListTicket | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  const obj = value as Record<string, unknown>;
  const ticket =
    readString(obj['ticket']) ||
    readString(obj['ticket_title']) ||
    readString(obj['ticketTitle']) ||
    readString(obj['task']) ||
    readString(obj['task_name']) ||
    readString(obj['taskName']) ||
    readString(obj['title']) ||
    readString(obj['name']);
  if (!ticket) {
    return null;
  }

  const item: AiHelpDataItemListTicket = { ticket };
  const project = readOptionalString(obj['project'] ?? obj['project_name'] ?? obj['projectName']);
  if (project) {
    item.project = project;
  }

  const ticketType = readOptionalString(
    obj['ticket_type'] ?? obj['ticketType'] ?? obj['type']
  );
  if (ticketType) {
    item.ticketType = ticketType;
  }

  const reason = readOptionalString(obj['reason'] ?? obj['note'] ?? obj['comment']);
  if (reason) {
    item.reason = reason;
  }

  const link = readOptionalString(obj['link'] ?? obj['url']);
  if (link) {
    item.link = link;
  }

  return item;
}

/** Spoken / copy line for a nested `tickets` row. */
export function formatItemListTicketLine(ticket: AiHelpDataItemListTicket): string {
  const bits: string[] = [ticket.ticket];
  if (ticket.ticketType) {
    bits.push(ticket.ticketType);
  }
  if (ticket.project) {
    bits.push(ticket.project);
  }
  if (ticket.reason) {
    bits.push(ticket.reason);
  }
  return bits.join(' — ');
}

function parseDataItemListOverProjects(value: unknown): AiHelpDataItemListOverProject[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => parseDataItemListOverProject(entry))
    .filter((item): item is AiHelpDataItemListOverProject => item !== null);
}

function parseDataItemListOverProject(value: unknown): AiHelpDataItemListOverProject | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  const obj = value as Record<string, unknown>;
  const project =
    readString(obj['project']) ||
    readString(obj['project_name']) ||
    readString(obj['name']) ||
    readString(obj['title']);
  if (!project) {
    return null;
  }

  const item: AiHelpDataItemListOverProject = { project };
  const monthlyHours = readOptionalNumber(obj['monthly_hours'] ?? obj['monthlyHours']);
  const usedHours = readOptionalNumber(obj['used_hours'] ?? obj['usedHours']);
  const overage = readOptionalNumber(obj['overage']);
  const utilizationPct = readOptionalNumber(
    obj['utilization_pct'] ?? obj['utilizationPct'] ?? obj['percent']
  );

  if (monthlyHours != null) {
    item.monthlyHours = monthlyHours;
  }
  if (usedHours != null) {
    item.usedHours = usedHours;
  }
  if (overage != null) {
    item.overage = overage;
  }
  if (utilizationPct != null) {
    item.utilizationPct = utilizationPct;
  }

  return item;
}

/** Spoken / copy line for a nested `over_projects` row. */
export function formatItemListOverProjectLine(project: AiHelpDataItemListOverProject): string {
  const bits: string[] = [];
  if (project.usedHours != null && project.monthlyHours != null) {
    bits.push(`${formatHours(project.usedHours)} used of ${formatHours(project.monthlyHours)}`);
  } else {
    if (project.usedHours != null) {
      bits.push(`${formatHours(project.usedHours)} used`);
    }
    if (project.monthlyHours != null) {
      bits.push(`${formatHours(project.monthlyHours)} monthly`);
    }
  }
  if (project.overage != null) {
    bits.push(`${formatHours(project.overage)} over`);
  }
  if (project.utilizationPct != null) {
    bits.push(formatPercentComplete(project.utilizationPct));
  }
  return bits.length ? `${project.project}: ${bits.join(', ')}` : project.project;
}

function readStatCardValue(value: unknown): string | number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim()) {
    return value.trim();
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  return null;
}

function parseStatCardTone(value: unknown): AiHelpDataStatTone {
  const tone = readString(value).toLowerCase();
  if (tone === 'red' || tone === 'danger' || tone === 'error') {
    return 'red';
  }
  if (tone === 'green' || tone === 'success') {
    return 'green';
  }
  if (tone === 'amber' || tone === 'orange' || tone === 'warning') {
    return 'amber';
  }
  return 'neutral';
}

function parseDataProjectsOverEstimate(
  raw: Record<string, unknown>
): AiHelpDataProjectsOverEstimateContent | null {
  const count = readOptionalNumber(raw['count']);
  const overPercent = readOptionalNumber(raw['over_percent']);
  const truncated = readOptionalBoolean(raw['truncated']);
  const matches = parseDataProjectsOverEstimateItems(raw['matches']);

  if (count == null && overPercent == null && truncated == null && matches.length === 0) {
    return null;
  }

  return {
    count,
    overPercent,
    truncated,
    matches
  };
}

function parseDataProjectsOverEstimateItems(
  value: unknown
): AiHelpDataProjectsOverEstimateItem[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => parseDataProjectsOverEstimateItem(entry))
    .filter((item): item is AiHelpDataProjectsOverEstimateItem => item !== null);
}

function parseDataProjectsOverEstimateItem(
  value: unknown
): AiHelpDataProjectsOverEstimateItem | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const obj = value as Record<string, unknown>;
  const projectName = readString(obj['project_name']);
  if (!projectName) {
    return null;
  }

  return {
    projectName,
    owner: readOptionalString(obj['owner']),
    estimatedHours: readOptionalNumber(obj['estimated_hours']),
    actualHours: readOptionalNumber(obj['actual_hours']),
    overrunPercent: readOptionalNumber(obj['overrun_percent'])
  };
}

function parseDataProjects(value: unknown): AiHelpDataProjectItem[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => parseDataProject(entry))
    .filter((item): item is AiHelpDataProjectItem => item !== null);
}

function parseDataProjectTasks(raw: Record<string, unknown>): {
  projectName?: string;
  tasks: Array<{
    taskName: string;
    status?: string;
    due?: string;
    assignees?: string;
  }>;
} | null {
  const projectName = readOptionalString(raw['project_name']);
  const tasks = parseDataProjectTaskItems(raw['tasks']);

  if (!projectName && tasks.length === 0) {
    return null;
  }

  return {
    projectName,
    tasks
  };
}

function parseDataProjectTaskItems(
  value: unknown
): Array<{
  taskName: string;
  status?: string;
  due?: string;
  assignees?: string;
}> {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => parseDataProjectTaskItem(entry))
    .filter(
      (
        item
      ): item is {
        taskName: string;
        status?: string;
        due?: string;
        assignees?: string;
      } => item !== null
    );
}

function parseDataProjectTaskItem(value: unknown): {
  taskName: string;
  status?: string;
  due?: string;
  assignees?: string;
} | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const obj = value as Record<string, unknown>;
  const taskName = readString(obj['task_name']);
  if (!taskName) {
    return null;
  }

  return {
    taskName,
    status: readOptionalString(obj['status']),
    due: readOptionalString(obj['due']),
    assignees: readOptionalString(obj['assignees'])
  };
}

function parseDataProjectTaskBreakdown(
  raw: Record<string, unknown>
): AiHelpDataProjectTaskBreakdownContent | null {
  const projectName = readOptionalString(raw['project_name']);
  const totalTasks = readOptionalNumber(raw['total_tasks']);
  const completedTasks = readOptionalNumber(raw['completed_tasks']);
  const remainingTasks = readOptionalNumber(raw['remaining_tasks']);
  const truncated = readOptionalBoolean(raw['truncated']);
  const remaining = parseDataProjectTaskBreakdownItems(raw['remaining'] ?? raw['tasks']);

  if (
    !projectName &&
    remaining.length === 0 &&
    totalTasks == null &&
    completedTasks == null &&
    remainingTasks == null
  ) {
    return null;
  }

  return {
    projectName,
    totalTasks,
    completedTasks,
    remainingTasks,
    remaining,
    truncated
  };
}

function parseDataProjectTaskBreakdownItems(
  value: unknown
): AiHelpDataProjectTaskBreakdownItem[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => parseDataProjectTaskBreakdownItem(entry))
    .filter((item): item is AiHelpDataProjectTaskBreakdownItem => item !== null);
}

function parseDataProjectTaskBreakdownItem(
  value: unknown
): AiHelpDataProjectTaskBreakdownItem | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const obj = value as Record<string, unknown>;
  const taskName = readString(obj['task_name']);
  if (!taskName) {
    return null;
  }

  return {
    taskName,
    status: readOptionalString(obj['status']),
    percentComplete: readPercentComplete(obj['percent_complete']),
    assignees: readOptionalString(obj['assignees'])
  };
}

function parseDataOverdueTasks(raw: Record<string, unknown>): AiHelpDataOverdueTasksContent | null {
  const scopeLabel =
    readOptionalString(raw['scope_label']) ??
    readOptionalString(raw['team']) ??
    readOptionalString(raw['user']) ??
    readOptionalString(raw['person']) ??
    readOptionalString(raw['name']);
  const count = readOptionalNumber(raw['count']) ?? readOptionalNumber(raw['total_overdue']);
  const shown =
    readOptionalNumber(raw['shown']) ??
    readOptionalNumber(raw['returned']) ??
    readOptionalNumber(raw['returned_count']);
  const onDate = readOptionalString(raw['on_date']);
  const truncated = readOptionalBoolean(raw['truncated']);
  const tasks = parseDataOverdueTaskItems(raw['tasks']);

  if (!scopeLabel && count == null && shown == null && !onDate && truncated == null && tasks.length === 0) {
    return null;
  }

  return {
    scopeLabel,
    count,
    shown,
    onDate,
    truncated,
    tasks
  };
}

function parseDataOverdueReqsIssues(
  raw: Record<string, unknown>
): AiHelpDataOverdueReqsIssuesContent | null {
  const itemType = readOptionalString(raw['item_type']) ?? readOptionalString(raw['itemType']);
  const scopeLabel = readOptionalString(raw['scope_label']) ?? readOptionalString(raw['scopeLabel']);
  const count = readOptionalNumber(raw['count']);
  const truncated = readOptionalBoolean(raw['truncated']);
  const items = parseDataOverdueReqIssueItems(raw['items']);

  if (!itemType && !scopeLabel && count == null && truncated == null && items.length === 0) {
    return null;
  }

  return {
    itemType,
    scopeLabel,
    count,
    truncated,
    items
  };
}

function parseDataOverdueReqIssueItems(value: unknown): AiHelpDataOverdueReqIssueItem[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => parseDataOverdueReqIssueItem(entry))
    .filter((item): item is AiHelpDataOverdueReqIssueItem => item !== null);
}

function parseDataOverdueReqIssueItem(value: unknown): AiHelpDataOverdueReqIssueItem | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const obj = value as Record<string, unknown>;
  const name = readString(obj['name']) || readString(obj['title']) || readString(obj['request']);
  if (!name) {
    return null;
  }

  return {
    name,
    status: readOptionalString(obj['status']),
    project: readOptionalString(obj['project']),
    dueDate: readOptionalString(obj['due_date']) ?? readOptionalString(obj['dueDate']),
    daysOverdue: readOptionalNumber(obj['days_overdue']) ?? readOptionalNumber(obj['daysOverdue']),
    link: readOptionalString(obj['link'])
  };
}

function parseDataPriorityTasks(raw: Record<string, unknown>): AiHelpDataPriorityTasksContent | null {
  const scopeLabel = readOptionalString(raw['scope_label']);
  const count = readOptionalNumber(raw['count']);
  const truncated = readOptionalBoolean(raw['truncated']);
  const tasks = parseDataPriorityTaskItems(raw['tasks']);

  if (!scopeLabel && count == null && truncated == null && tasks.length === 0) {
    return null;
  }

  return {
    scopeLabel,
    count,
    truncated,
    tasks
  };
}

function parseDataPendingServiceTasks(
  raw: Record<string, unknown>
): AiHelpDataPendingServiceTasksContent | null {
  const user = readOptionalString(raw['user']);
  const serviceGroup =
    readOptionalString(raw['service_group']) ?? readOptionalString(raw['serviceGroup']);
  const count = readOptionalNumber(raw['count']);
  const shown = readOptionalNumber(raw['shown']);
  const truncated = readOptionalBoolean(raw['truncated']);
  const tasks = parseDataPendingServiceTaskItems(raw['tasks']);

  if (
    !user &&
    !serviceGroup &&
    count == null &&
    shown == null &&
    truncated == null &&
    tasks.length === 0
  ) {
    return null;
  }

  return {
    user,
    serviceGroup,
    count,
    shown,
    truncated,
    tasks
  };
}

function parseDataPendingServiceTaskItems(value: unknown): AiHelpDataPendingServiceTaskItem[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => parseDataPendingServiceTaskItem(entry))
    .filter((item): item is AiHelpDataPendingServiceTaskItem => item !== null);
}

function parseDataPendingServiceTaskItem(value: unknown): AiHelpDataPendingServiceTaskItem | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const obj = value as Record<string, unknown>;
  const taskName =
    readString(obj['task_name']) || readString(obj['name']) || readString(obj['task']);
  if (!taskName) {
    return null;
  }

  return {
    taskId: readOptionalString(obj['task_id']) ?? readOptionalString(obj['taskId']),
    taskName,
    status: readOptionalString(obj['status']),
    projectName:
      readOptionalString(obj['project_name']) ??
      readOptionalString(obj['projectName']) ??
      readOptionalString(obj['project']),
    serviceName:
      readOptionalString(obj['service_name']) ??
      readOptionalString(obj['serviceName']) ??
      readOptionalString(obj['service']),
    dueDate: readOptionalString(obj['due_date']) ?? readOptionalString(obj['dueDate']) ?? readOptionalString(obj['due'])
  };
}

function parseDataPriorityTaskItems(value: unknown): AiHelpDataPriorityTasksContent['tasks'] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => parseDataPriorityTaskItem(entry))
    .filter((item): item is AiHelpDataPriorityTasksContent['tasks'][number] => item !== null);
}

function parseDataPriorityTaskItem(
  value: unknown
): AiHelpDataPriorityTasksContent['tasks'][number] | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const obj = value as Record<string, unknown>;
  const name =
    readString(obj['name']) || readString(obj['task_name']) || readString(obj['item_name']);
  if (!name) {
    return null;
  }

  return {
    name,
    status: readOptionalString(obj['status']),
    dueDate: readOptionalString(obj['due_date']) ?? readOptionalString(obj['due']),
    project: readOptionalString(obj['project']),
    assignees: readAssignees(obj['assignees'])
  };
}

function readAssignees(value: unknown): string | undefined {
  if (typeof value === 'string') {
    return readOptionalString(value);
  }

  if (Array.isArray(value)) {
    const names = value
      .map((entry) => (typeof entry === 'string' ? entry.trim() : ''))
      .filter((entry) => entry.length > 0);
    return names.length > 0 ? names.join(', ') : undefined;
  }

  return undefined;
}

function parseDataItemsDue(raw: Record<string, unknown>): AiHelpDataItemsDueContent | null {
  const when = readOptionalString(raw['when']);
  const start = readOptionalString(raw['start']);
  const end = readOptionalString(raw['end']);
  const count = readOptionalNumber(raw['count']);
  const truncated = readOptionalBoolean(raw['truncated']);
  const items = parseDataItemsDueItems(raw['items']);

  if (!when && !start && !end && count == null && truncated == null && items.length === 0) {
    return null;
  }

  return {
    when,
    start,
    end,
    count,
    truncated,
    items,
    groups: groupItemsDueItems(items)
  };
}

function groupItemsDueItems(items: AiHelpDataItemsDueItem[]): AiHelpDataItemsDueGroup[] {
  const groups: AiHelpDataItemsDueGroup[] = [];
  const indexByType = new Map<string, number>();

  items.forEach((item) => {
    const type = item.type?.trim() || 'Item';
    const existing = indexByType.get(type);
    if (existing != null) {
      groups[existing].items.push(item);
      return;
    }
    indexByType.set(type, groups.length);
    groups.push({ type, items: [item] });
  });

  return groups;
}

function parseDataItemsDueItems(value: unknown): AiHelpDataItemsDueItem[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => parseDataItemsDueItem(entry))
    .filter((item): item is AiHelpDataItemsDueItem => item !== null);
}

function parseDataItemsDueItem(value: unknown): AiHelpDataItemsDueItem | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const obj = value as Record<string, unknown>;
  const name =
    readString(obj['name']) || readString(obj['task_name']) || readString(obj['item_name']);
  if (!name) {
    return null;
  }

  return {
    name,
    type: readOptionalString(obj['type']) ?? readOptionalString(obj['item_type']),
    status: readOptionalString(obj['status']),
    dueDate: readOptionalString(obj['due_date']) ?? readOptionalString(obj['due']),
    project: readOptionalString(obj['project']),
    assignees: readOptionalString(obj['assignees'])
  };
}

function parseDataWorkToday(raw: Record<string, unknown>): AiHelpDataWorkTodayContent {
  const doNow = parseDataWorkTodayItems(raw['do_now']);
  const comingUp = parseDataWorkTodayItems(raw['coming_up']);
  const groups: AiHelpDataWorkTodayGroup[] = [];

  if (doNow.length) {
    groups.push({ key: 'do_now', label: 'Do now', items: doNow });
  }
  if (comingUp.length) {
    groups.push({ key: 'coming_up', label: 'Coming up', items: comingUp });
  }

  return {
    hasWork: readOptionalBoolean(raw['has_work']),
    doNow,
    comingUp,
    groups
  };
}

function parseDataWorkTodayItems(value: unknown): AiHelpDataWorkTodayItem[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => parseDataWorkTodayItem(entry))
    .filter((item): item is AiHelpDataWorkTodayItem => item !== null);
}

function parseDataWorkTodayItem(value: unknown): AiHelpDataWorkTodayItem | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const obj = value as Record<string, unknown>;
  const name =
    readString(obj['name']) || readString(obj['task_name']) || readString(obj['item_name']);
  if (!name) {
    return null;
  }

  return {
    name,
    type: readOptionalString(obj['type']) ?? readOptionalString(obj['item_type']),
    status: readOptionalString(obj['status']),
    dueDate: readOptionalString(obj['due_date']) ?? readOptionalString(obj['due']),
    project: readOptionalString(obj['project']),
    daysOverdue: readOptionalNumber(obj['days_overdue']),
    link: readOptionalString(obj['link'])
  };
}

function parseDataOverdueTaskItems(value: unknown): AiHelpDataOverdueTasksContent['tasks'] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => parseDataOverdueTaskItem(entry))
    .filter((item): item is AiHelpDataOverdueTasksContent['tasks'][number] => item !== null);
}

function parseDataCompletedItems(raw: Record<string, unknown>): AiHelpDataCompletedItemsContent | null {
  const team = readOptionalString(raw['Team']) ?? readOptionalString(raw['team']);
  const totalCompleted = readOptionalNumber(raw['total_completed']);
  const groups = parseDataCompletedItemGroups(raw['by_type']);

  if (!team && totalCompleted == null && groups.length === 0) {
    return null;
  }

  return {
    team,
    totalCompleted,
    groups
  };
}

function parseDataCompletedItemGroups(value: unknown): AiHelpDataCompletedItemGroup[] {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return [];
  }

  return Object.entries(value as Record<string, unknown>)
    .map(([type, items]) => {
      const parsedItems = parseDataCompletedItemList(items);
      if (parsedItems.length === 0) {
        return null;
      }
      return { type, items: parsedItems };
    })
    .filter((group): group is AiHelpDataCompletedItemGroup => group !== null);
}

function parseDataCompletedItemList(value: unknown): AiHelpDataCompletedItem[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => parseDataCompletedItem(entry))
    .filter((item): item is AiHelpDataCompletedItem => item !== null);
}

function parseDataCurrentWork(raw: Record<string, unknown>): AiHelpDataCurrentWorkContent {
  const breakdownRaw = raw['breakdown'];
  let breakdown: Record<string, number> | undefined;
  if (breakdownRaw && typeof breakdownRaw === 'object' && !Array.isArray(breakdownRaw)) {
    const parsed: Record<string, number> = {};
    Object.entries(breakdownRaw as Record<string, unknown>).forEach(([key, value]) => {
      const count = readOptionalNumber(value);
      if (key.trim() && count != null) {
        parsed[key] = count;
      }
    });
    if (Object.keys(parsed).length > 0) {
      breakdown = parsed;
    }
  }

  const items = parseDataCurrentWorkItems(raw['items']);

  return {
    user: readOptionalString(raw['user']),
    count: readOptionalNumber(raw['count']),
    breakdown,
    truncated: readOptionalBoolean(raw['truncated']),
    items,
    groups: groupCurrentWorkItems(items)
  };
}

function groupCurrentWorkItems(items: AiHelpDataCurrentWorkItem[]): AiHelpDataCurrentWorkGroup[] {
  const groups: AiHelpDataCurrentWorkGroup[] = [];
  const indexByType = new Map<string, number>();

  items.forEach((item) => {
    const type = item.type?.trim() || 'Item';
    const existing = indexByType.get(type);
    if (existing != null) {
      groups[existing].items.push(item);
      return;
    }
    indexByType.set(type, groups.length);
    groups.push({ type, items: [item] });
  });

  return groups;
}

function parseDataCurrentWorkItems(value: unknown): AiHelpDataCurrentWorkItem[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => parseDataCurrentWorkItem(entry))
    .filter((item): item is AiHelpDataCurrentWorkItem => item !== null);
}

function parseDataCurrentWorkItem(value: unknown): AiHelpDataCurrentWorkItem | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const obj = value as Record<string, unknown>;
  const name =
    readString(obj['name']) || readString(obj['task_name']) || readString(obj['item_name']);
  if (!name) {
    return null;
  }

  return {
    name,
    type: readOptionalString(obj['type']) ?? readOptionalString(obj['item_type'])
  };
}

function parseDataCompletedItem(value: unknown): AiHelpDataCompletedItem | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const obj = value as Record<string, unknown>;
  const name =
    readString(obj['name']) || readString(obj['task_name']) || readString(obj['item_name']);
  if (!name) {
    return null;
  }

  return {
    name,
    status: readOptionalString(obj['status']),
    completedOn: readOptionalString(obj['completed_on']) ?? readOptionalString(obj['completed_date']),
    assignees: readOptionalString(obj['assignees'])
  };
}

function parseDataOverdueTaskItem(value: unknown): AiHelpDataOverdueTasksContent['tasks'][number] | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const obj = value as Record<string, unknown>;
  // API variants: task_name (classic), task_title (my_overdue / team_overdue), task, name
  const taskName =
    readString(obj['task_name']) ||
    readString(obj['task_title']) ||
    readString(obj['title']) ||
    readString(obj['task']) ||
    readString(obj['name']);
  if (!taskName) {
    return null;
  }

  return {
    taskName,
    status: readOptionalString(obj['status']),
    daysOverdue:
      readOptionalNumber(obj['days_overdue']) ?? readOptionalNumber(obj['daysOverdue']),
    dueDate:
      readOptionalString(obj['due_date']) ??
      readOptionalString(obj['dueDate']) ??
      readOptionalString(obj['due']),
    project:
      readOptionalString(obj['project']) ??
      readOptionalString(obj['project_name']) ??
      readOptionalString(obj['projectName']),
    assignees: readAssignees(obj['assignees']),
    plannedHours:
      readOptionalNumber(obj['planned_hours']) ?? readOptionalNumber(obj['plannedHours']),
    link: readOptionalString(obj['link'])
  };
}

function parseDataProject(value: unknown): AiHelpDataProjectItem | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const obj = value as Record<string, unknown>;
  const projectName = readString(obj['project_name']) || readString(obj['name']);
  if (!projectName) {
    return null;
  }

  const projectId = readString(obj['project_id']) || undefined;
  const daysOverdue = readOptionalNumber(obj['days_overdue']);
  const overdueCount = readOptionalNumber(obj['overdue_count']);
  const dueDate = readOptionalString(obj['due_date']);
  const projectedCompletion = readOptionalString(obj['projected_completion']);

  return {
    projectId,
    projectName,
    status: readOptionalString(obj['status']),
    percentComplete: readPercentComplete(obj['percent_complete']),
    owner: readOptionalString(obj['owner']),
    daysOverdue,
    overdueCount,
    dueDate,
    projectedCompletion
  };
}

function readPercentComplete(value: unknown): number | undefined {
  const parsed =
    typeof value === 'number' && Number.isFinite(value)
      ? value
      : typeof value === 'string' && value.trim()
        ? Number(value.trim())
        : Number.NaN;

  if (!Number.isFinite(parsed)) {
    return undefined;
  }

  // Some payloads send 0–1 fractions (0.25); others send 0–100 percents.
  if (parsed > 0 && parsed < 1) {
    return Math.round(parsed * 10000) / 100;
  }

  return parsed;
}

function readOptionalNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value.trim());
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

function readOptionalBoolean(value: unknown): boolean | undefined {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim().toLowerCase();
    if (trimmed === 'true') return true;
    if (trimmed === 'false') return false;
  }
  return undefined;
}

function parseDataUpdates(raw: Record<string, unknown>): {
  user?: string;
  count?: number;
  detail?: boolean;
  updates?: AiHelpDataUpdateItem[];
} | null {
  const user = readOptionalString(raw['user']);
  const count = readOptionalNumber(raw['count']);
  const detail = readOptionalBoolean(raw['detail']);
  const updates = parseDataUpdateItems(raw['updates']);

  if (!user && count == null && detail == null && updates.length === 0) {
    return null;
  }

  return {
    user,
    count,
    detail,
    updates: updates.length ? updates : undefined
  };
}

function parseDataUpdateItems(value: unknown): AiHelpDataUpdateItem[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => parseDataUpdateItem(entry))
    .filter((item): item is AiHelpDataUpdateItem => item !== null);
}

function parseDataUpdateItem(value: unknown): AiHelpDataUpdateItem | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const obj = value as Record<string, unknown>;
  const type = readOptionalString(obj['type']);
  const on = readOptionalString(obj['on']);
  const date = readOptionalString(obj['date']);
  const text = readOptionalString(obj['text']);

  if (!type && !on && !date && !text) {
    return null;
  }

  return {
    type,
    on,
    date,
    text
  };
}

function parseDataCommentSearch(raw: Record<string, unknown>): AiHelpDataCommentSearchContent | null {
  const keyword = readOptionalString(raw['keyword']);
  const total = readOptionalNumber(raw['total']);
  const hasMore = readOptionalBoolean(raw['has_more']);
  const matches = parseDataCommentSearchItems(raw['matches']);

  if (!keyword && total == null && hasMore == null && matches.length === 0) {
    return null;
  }

  return {
    keyword,
    total,
    hasMore,
    matches
  };
}

function parseDataMyApprovals(raw: Record<string, unknown>): AiHelpDataMyApprovalsContent | null {
  const total = readOptionalNumber(raw['total']);
  const shown = readOptionalNumber(raw['shown']);
  const hasMore = readOptionalBoolean(raw['has_more']);
  const approvals = parseDataMyApprovalItems(raw['approvals']);

  if (total == null && shown == null && hasMore == null && approvals.length === 0) {
    return null;
  }

  return {
    total,
    shown,
    hasMore,
    approvals,
    groups: groupMyApprovalItems(approvals)
  };
}

function parseDataClientRequests(raw: Record<string, unknown>): AiHelpDataClientRequestsContent | null {
  const client = readOptionalString(raw['client']);
  const count = readOptionalNumber(raw['count']);
  const truncated = readOptionalBoolean(raw['truncated']);
  const requests = parseDataClientRequestItems(raw['requests']);

  if (client == null && count == null && truncated == null && requests.length === 0) {
    return null;
  }

  return {
    client,
    count,
    truncated,
    requests
  };
}

function parseDataClientRequestItems(value: unknown): AiHelpDataClientRequestItem[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => parseDataClientRequestItem(entry))
    .filter((item): item is AiHelpDataClientRequestItem => item !== null);
}

function parseDataClientRequestItem(value: unknown): AiHelpDataClientRequestItem | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const obj = value as Record<string, unknown>;
  const request =
    readString(obj['request']) || readString(obj['title']) || readString(obj['name']);
  if (!request) {
    return null;
  }

  return {
    request,
    status: readOptionalString(obj['status']),
    project: readOptionalString(obj['project']),
    dueDate: readOptionalString(obj['due_date']) ?? readOptionalString(obj['dueDate']),
    link: readOptionalString(obj['link'])
  };
}

function groupMyApprovalItems(items: AiHelpDataMyApprovalItem[]): AiHelpDataMyApprovalGroup[] {
  const groups: AiHelpDataMyApprovalGroup[] = [];
  const indexByType = new Map<string, number>();

  items.forEach((item) => {
    const type = item.type?.trim() || 'Approval';
    const existing = indexByType.get(type);
    if (existing != null) {
      groups[existing].items.push(item);
      return;
    }
    indexByType.set(type, groups.length);
    groups.push({ type, items: [item] });
  });

  return groups;
}

function parseDataMyApprovalItems(value: unknown): AiHelpDataMyApprovalItem[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => parseDataMyApprovalItem(entry))
    .filter((item): item is AiHelpDataMyApprovalItem => item !== null);
}

function parseDataMyApprovalItem(value: unknown): AiHelpDataMyApprovalItem | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const obj = value as Record<string, unknown>;
  const title = readString(obj['title']) || readString(obj['name']);
  if (!title) {
    return null;
  }

  return {
    title,
    type: readOptionalString(obj['type']),
    project: readOptionalString(obj['project']),
    requestedOn: readOptionalString(obj['requested_on']) ?? readOptionalString(obj['requestedOn']),
    link: readOptionalString(obj['link'])
  };
}

function parseDataCommentSearchItems(value: unknown): AiHelpDataCommentSearchItem[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => parseDataCommentSearchItem(entry))
    .filter((item): item is AiHelpDataCommentSearchItem => item !== null);
}

function parseDataCommentSearchItem(value: unknown): AiHelpDataCommentSearchItem | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const obj = value as Record<string, unknown>;
  const snippet = sanitizeCommentSnippet(readOptionalString(obj['snippet']));
  const project = readOptionalString(obj['project']);
  const type = readOptionalString(obj['type']);
  const on = readOptionalString(obj['on']);
  const by = readOptionalString(obj['by']);
  const date = readOptionalString(obj['date']);

  if (!snippet && !project && !type && !on && !by && !date) {
    return null;
  }

  return {
    snippet,
    project,
    type,
    on,
    by,
    date
  };
}

function sanitizeCommentSnippet(value: string | undefined): string | undefined {
  if (!value) {
    return undefined;
  }

  const cleaned = value.replace(/\uFEFF/g, '').replace(/\s+/g, ' ').trim();
  return cleaned || undefined;
}

function parseDataRanking(
  raw: Record<string, unknown>,
  options: { isAiHoursRanking?: boolean; isTaskLoadRanking?: boolean } = {}
): AiHelpDataRankingContent | null {
  const { isAiHoursRanking = false, isTaskLoadRanking = false } = options;
  const scope =
    readOptionalString(raw['scope']) ??
    (isTaskLoadRanking ? readOptionalString(raw['scope_label']) : undefined) ??
    (isAiHoursRanking ? readOptionalString(raw['date_label']) : undefined);
  const peopleCounted =
    readOptionalNumber(raw['people_counted']) ??
    (isTaskLoadRanking ? readOptionalNumber(raw['people_count']) : undefined) ??
    (isAiHoursRanking ? readOptionalNumber(raw['team_count']) : undefined);
  const countLabel = isAiHoursRanking && peopleCounted != null ? 'Teams' : undefined;
  const itemLabel = isTaskLoadRanking ? readOptionalString(raw['item_label']) : undefined;
  const ranking = parseDataRankingItems(raw['ranking'], isTaskLoadRanking);

  // Empty ranking is valid (e.g. nobody matched) when scope/people_counted is present.
  if (!scope && peopleCounted == null && ranking.length === 0) {
    return null;
  }

  return {
    scope,
    peopleCounted,
    countLabel,
    itemLabel,
    ranking
  };
}

function parseDataRankingItems(
  value: unknown,
  isTaskLoadRanking = false
): AiHelpDataRankingItem[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => parseDataRankingItem(entry, isTaskLoadRanking))
    .filter((item): item is AiHelpDataRankingItem => item !== null);
}

function parseDataRankingItem(
  value: unknown,
  isTaskLoadRanking = false
): AiHelpDataRankingItem | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const obj = value as Record<string, unknown>;
  const name = readString(obj['name']) || readString(obj['team']);
  if (!name) {
    return null;
  }

  const percent = readRankingPercent(obj['percent']);

  if (isTaskLoadRanking) {
    const openItems = readOptionalNumber(obj['open_items']);
    if (openItems == null) {
      return null;
    }

    return { name, openItems, percent };
  }

  const hours = readOptionalNumber(obj['hours']) ?? readOptionalNumber(obj['ai_hours']);
  if (hours == null) {
    return null;
  }

  return { name, hours, percent };
}

/** Ranking share is already 0–100 (including values like 0.5). Do not treat 0–1 as a fraction. */
function readRankingPercent(value: unknown): number | undefined {
  const parsed = readOptionalNumber(value);
  if (parsed == null) {
    return undefined;
  }

  return Math.max(0, Math.min(100, parsed));
}

export function formatRankingEntryValue(
  entry: { hours?: number; openItems?: number; days?: number },
  itemLabel?: string
): string {
  if (entry.openItems != null) {
    return itemLabel ? `${entry.openItems} ${itemLabel}` : String(entry.openItems);
  }

  if (entry.days != null) {
    return `${entry.days} ${entry.days === 1 ? 'day' : 'days'}`;
  }

  return formatHours(entry.hours ?? 0);
}

function parseDataUser(raw: Record<string, unknown>): {
  user?: string;
  totalHours?: number;
  byTask: Array<{ item: string; type?: string; hours: number }>;
} | null {
  const user = readOptionalString(raw['user']);
  const totalHours = readOptionalNumber(raw['total_hours']);
  const byTask = parseDataUserTaskItems(raw['by_task']);

  if (!user && totalHours == null && byTask.length === 0) {
    return null;
  }

  return {
    user,
    totalHours,
    byTask
  };
}

function parseDataUserTaskItems(
  value: unknown
): Array<{ item: string; type?: string; hours: number }> {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => parseDataUserTaskItem(entry))
    .filter((item): item is { item: string; type?: string; hours: number } => item !== null);
}

function parseDataUserTaskItem(
  value: unknown
): { item: string; type?: string; hours: number } | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const obj = value as Record<string, unknown>;
  const item = readString(obj['item']);
  const hours = readOptionalNumber(obj['hours']);
  if (!item || hours == null) {
    return null;
  }

  return {
    item,
    type: readOptionalString(obj['type']),
    hours
  };
}

function parseDataDailyHours(raw: Record<string, unknown>): {
  user?: string;
  totalHours?: number;
  truncated?: boolean;
  dayCount?: number;
  days: Array<{ date: string; hours: number }>;
} | null {
  const user = readOptionalString(raw['user']);
  const totalHours = readOptionalNumber(raw['total_hours']);
  const truncated = readOptionalBoolean(raw['truncated']);
  const dayCount = readOptionalNumber(raw['day_count']);
  const days = parseDataDailyHoursItems(raw['days']);

  if (!user && totalHours == null && truncated == null && dayCount == null && days.length === 0) {
    return null;
  }

  return {
    user,
    totalHours,
    truncated,
    dayCount,
    days
  };
}

function parseDataDailyHoursItems(value: unknown): Array<{ date: string; hours: number }> {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => parseDataDailyHoursItem(entry))
    .filter((item): item is { date: string; hours: number } => item !== null);
}

function parseDataDailyHoursItem(value: unknown): { date: string; hours: number } | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const obj = value as Record<string, unknown>;
  const date = readString(obj['date']);
  const hours = readOptionalNumber(obj['hours']);
  if (!date || hours == null) {
    return null;
  }

  return { date, hours };
}

function parseDataAiHoursTrend(raw: Record<string, unknown>): {
  scopeLabel?: string;
  weeksCount?: number;
  totalHours?: number;
  weeks: Array<{ weekStart: string; weekLabel: string; hours: number }>;
} | null {
  const scopeLabel = readOptionalString(raw['scope_label']);
  const weeksCount = readOptionalNumber(raw['weeks_count']);
  const totalHours = readOptionalNumber(raw['total_hours']);
  const weeks = parseDataAiHoursTrendWeeks(raw['weeks']);

  if (!scopeLabel && weeksCount == null && totalHours == null && weeks.length === 0) {
    return null;
  }

  return {
    scopeLabel,
    weeksCount,
    totalHours,
    weeks
  };
}

function parseDataAiHoursTrendWeeks(
  value: unknown
): Array<{ weekStart: string; weekLabel: string; hours: number }> {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => parseDataAiHoursTrendWeek(entry))
    .filter(
      (item): item is { weekStart: string; weekLabel: string; hours: number } => item !== null
    );
}

function parseDataAiHoursTrendWeek(
  value: unknown
): { weekStart: string; weekLabel: string; hours: number } | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const obj = value as Record<string, unknown>;
  const weekStart = readString(obj['week_start']);
  const weekLabel = readString(obj['week_label']) || weekStart;
  const hours = readOptionalNumber(obj['hours']);
  if (!weekStart || hours == null) {
    return null;
  }

  return { weekStart, weekLabel, hours };
}

function parseDataLeave(raw: Record<string, unknown>): {
  scope?: string;
  peopleCounted?: number;
  people: Array<{
    name: string;
    totalHours: number;
    days: Array<{ date: string; category?: string; hours: number }>;
  }>;
} | null {
  const scope = readOptionalString(raw['scope']);
  const peopleCounted = readOptionalNumber(raw['people_counted']);
  const people = parseDataLeavePeople(raw['people']);

  // Empty people is valid (e.g. nobody on leave) when scope/people_counted is present.
  if (!scope && peopleCounted == null && people.length === 0) {
    return null;
  }

  return {
    scope,
    peopleCounted,
    people
  };
}

function parseDataFreeHours(raw: Record<string, unknown>): {
  target?: string;
  isRole?: boolean;
  date?: string;
  capacityHours?: number;
  people: Array<{
    name: string;
    scheduledHours: number;
    freeHours: number;
    overbooked?: boolean;
  }>;
} | null {
  const target = readOptionalString(raw['target']);
  const isRole = readOptionalBoolean(raw['is_role']);
  const date = readOptionalString(raw['date']);
  const capacityHours = readOptionalNumber(raw['capacity_hours']);
  const people = parseDataFreeHoursPeople(raw['people']);

  if (!target && isRole == null && !date && capacityHours == null && people.length === 0) {
    return null;
  }

  return {
    target,
    isRole,
    date,
    capacityHours,
    people
  };
}

function parseDataFreeHoursPeople(
  value: unknown
): Array<{
  name: string;
  scheduledHours: number;
  freeHours: number;
  overbooked?: boolean;
}> {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => parseDataFreeHoursPerson(entry))
    .filter(
      (
        item
      ): item is {
        name: string;
        scheduledHours: number;
        freeHours: number;
        overbooked?: boolean;
      } => item !== null
    );
}

function parseDataFreeHoursPerson(value: unknown): {
  name: string;
  scheduledHours: number;
  freeHours: number;
  overbooked?: boolean;
} | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const obj = value as Record<string, unknown>;
  const name = readString(obj['name']);
  const scheduledHours = readOptionalNumber(obj['scheduled_hours']);
  const freeHours = readOptionalNumber(obj['free_hours']);
  if (!name || scheduledHours == null || freeHours == null) {
    return null;
  }

  return {
    name,
    scheduledHours,
    freeHours,
    overbooked: readOptionalBoolean(obj['overbooked'])
  };
}

function parseDataLeavePeople(
  value: unknown
): Array<{
  name: string;
  totalHours: number;
  days: Array<{ date: string; category?: string; hours: number }>;
}> {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => parseDataLeavePerson(entry))
    .filter(
      (
        item
      ): item is {
        name: string;
        totalHours: number;
        days: Array<{ date: string; category?: string; hours: number }>;
      } => item !== null
    );
}

function parseDataLeavePerson(value: unknown): {
  name: string;
  totalHours: number;
  days: Array<{ date: string; category?: string; hours: number }>;
} | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const obj = value as Record<string, unknown>;
  const name = readString(obj['name']);
  const totalHours = readOptionalNumber(obj['total_hours']);
  if (!name || totalHours == null) {
    return null;
  }

  return {
    name,
    totalHours,
    days: parseDataLeaveDays(obj['days'])
  };
}

function parseDataLeaveDays(
  value: unknown
): Array<{ date: string; category?: string; hours: number }> {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => parseDataLeaveDay(entry))
    .filter(
      (item): item is { date: string; category?: string; hours: number } => item !== null
    );
}

function parseDataLeaveDay(
  value: unknown
): { date: string; category?: string; hours: number } | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const obj = value as Record<string, unknown>;
  const date = readString(obj['date']);
  const hours = readOptionalNumber(obj['hours']);
  if (!date || hours == null) {
    return null;
  }

  return {
    date,
    category: readOptionalString(obj['category']),
    hours
  };
}

function parseDataTimesheetSubmission(
  raw: Record<string, unknown>
): {
  team?: string;
  week?: { from: string; to: string };
  everyoneSubmitted?: boolean;
  totalMembers?: number;
  submittedCount?: number;
  notSubmittedCount?: number;
  notSubmitted: Array<{ name: string; status?: string }>;
} | null {
  const team = readOptionalString(raw['team']);
  const week = parseDataTimesheetSubmissionWeek(raw['week']);
  const everyoneSubmitted = readOptionalBoolean(raw['everyone_submitted']);
  const totalMembers = readOptionalNumber(raw['total_members']);
  const submittedCount = readOptionalNumber(raw['submitted_count']);
  const notSubmittedCount = readOptionalNumber(raw['not_submitted_count']);
  const notSubmitted = parseDataTimesheetSubmissionPeople(raw['not_submitted']);

  if (
    !team &&
    !week &&
    everyoneSubmitted == null &&
    totalMembers == null &&
    submittedCount == null &&
    notSubmittedCount == null &&
    notSubmitted.length === 0
  ) {
    return null;
  }

  return {
    team,
    week,
    everyoneSubmitted,
    totalMembers,
    submittedCount,
    notSubmittedCount,
    notSubmitted
  };
}

function parseDataTimesheetSubmissionWeek(
  value: unknown
): { from: string; to: string } | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }

  const obj = value as Record<string, unknown>;
  const from = readString(obj['from']);
  const to = readString(obj['to']);
  if (!from || !to) {
    return undefined;
  }

  return { from, to };
}

function parseDataTimesheetSubmissionPeople(
  value: unknown
): Array<{ name: string; status?: string }> {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => parseDataTimesheetSubmissionPerson(entry))
    .filter((item): item is { name: string; status?: string } => item !== null);
}

function parseDataTimesheetSubmissionPerson(
  value: unknown
): { name: string; status?: string } | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const obj = value as Record<string, unknown>;
  const name = readString(obj['name']);
  if (!name) {
    return null;
  }

  return {
    name,
    status: readOptionalString(obj['status'])
  };
}

function parseDataClientBrief(raw: Record<string, unknown>): AiHelpDataClientBriefContent | null {
  const client = readOptionalString(raw['client']);
  const activeProjectCount = readOptionalNumber(raw['active_project_count']);
  const overdueTaskCount = readOptionalNumber(raw['overdue_task_count']);
  const hoursThisMonth = readOptionalNumber(raw['hours_this_month']);
  const monthLabel = readOptionalString(raw['month_label']);
  const projects = parseDataClientBriefProjects(raw['projects']);
  const recentActivity = parseStringArray(raw['recent_activity']);
  const talkingPoints = parseStringArray(raw['talking_points']);

  if (
    !client &&
    activeProjectCount == null &&
    overdueTaskCount == null &&
    hoursThisMonth == null &&
    !monthLabel &&
    projects.length === 0 &&
    recentActivity.length === 0 &&
    talkingPoints.length === 0
  ) {
    return null;
  }

  return {
    client,
    activeProjectCount,
    projects,
    overdueTaskCount,
    hoursThisMonth,
    monthLabel,
    recentActivity,
    talkingPoints
  };
}

function parseDataClientBriefProjects(value: unknown): AiHelpDataClientBriefProjectItem[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => parseDataClientBriefProject(entry))
    .filter((item): item is AiHelpDataClientBriefProjectItem => item !== null);
}

function parseDataClientBriefProject(value: unknown): AiHelpDataClientBriefProjectItem | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const obj = value as Record<string, unknown>;
  const projectName = readString(obj['name']) || readString(obj['project_name']);
  if (!projectName) {
    return null;
  }

  return {
    projectName,
    percentComplete: readPercentComplete(obj['percent_complete']),
    status: readOptionalString(obj['status']),
    waitingOn: readOptionalString(obj['waiting_on'])
  };
}

function parseStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => (typeof entry === 'string' ? entry.trim() : ''))
    .filter((entry) => entry.length > 0);
}

function parseDataRecord(raw: Record<string, unknown>): Record<string, AiHelpDataValue> {
  const data: Record<string, AiHelpDataValue> = {};

  const assignScalar = (key: string, value: unknown): void => {
    if (!key.trim()) {
      return;
    }
    if (value === null || typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      if (data[key] === undefined) {
        data[key] = value;
      }
    }
  };

  Object.entries(raw).forEach(([key, value]) => {
    assignScalar(key, value);

    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return;
    }

    Object.entries(value as Record<string, unknown>).forEach(([nestedKey, nestedValue]) => {
      assignScalar(nestedKey, nestedValue);
    });
  });

  return data;
}

function parseDataProjectHealth(raw: Record<string, unknown>): AiHelpDataProjectHealthContent | null {
  const projectName = readString(raw['project_name']);
  if (!projectName) {
    return null;
  }

  const hoursRaw = raw['hours'];
  let hours: AiHelpDataProjectHealthContent['hours'];
  if (hoursRaw && typeof hoursRaw === 'object' && !Array.isArray(hoursRaw)) {
    const hoursObj = hoursRaw as Record<string, unknown>;
    const parsedHours = {
      totalHours: readOptionalNumber(hoursObj['total_hours']),
      loggedHours: readOptionalNumber(hoursObj['logged_hours']),
      pendingHours: readOptionalNumber(hoursObj['pending_hours'])
    };
    if (
      parsedHours.totalHours != null ||
      parsedHours.loggedHours != null ||
      parsedHours.pendingHours != null
    ) {
      hours = parsedHours;
    }
  }

  return {
    projectName,
    track: readOptionalString(raw['track']),
    daysRemaining: readOptionalNumber(raw['days_remaining']),
    percentComplete: readPercentComplete(raw['percent_complete']),
    status: readOptionalString(raw['status']),
    dueDate: readOptionalString(raw['due_date']),
    originalDueDate: readOptionalString(raw['original_due_date']),
    owner: readOptionalString(raw['owner']),
    openTaskCount: readOptionalNumber(raw['open_task_count']),
    hours
  };
}

export function isCompleteProjectHealth(health: AiHelpDataProjectHealthContent): boolean {
  const haystack = `${health.status ?? ''} ${health.track ?? ''}`.toLowerCase();
  return haystack.includes('complete') || haystack.includes('closed');
}

function extractClarifyText(obj: Record<string, unknown>): string {
  const fields = ['message', 'question', 'intro', 'text', 'reply', 'answer'];
  for (const field of fields) {
    const value = obj[field];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  return '';
}

function extractGuide(raw: unknown): AiHelpGuideContent | null {
  const candidates = collectObjects(raw);
  for (const obj of candidates) {
    const parsed = parseGuideObject(obj);
    if (parsed) {
      return parsed;
    }
  }
  return null;
}

function extractIsAudio(raw: unknown): boolean {
  for (const obj of collectObjects(raw)) {
    if (obj['isAudio'] === true) {
      return true;
    }
  }
  return false;
}

function extractIntroText(raw: unknown): string {
  for (const obj of collectObjects(raw)) {
    const intro = readString(obj['intro']);
    if (intro) {
      return intro;
    }
  }
  return '';
}

function collectObjects(raw: unknown): Record<string, unknown>[] {
  if (!raw || typeof raw !== 'object') {
    return [];
  }

  const obj = raw as Record<string, unknown>;
  const objects = [obj];

  const data = obj['data'];
  if (data && typeof data === 'object') {
    objects.push(data as Record<string, unknown>);
  }

  return objects;
}

function parseGuideObject(obj: Record<string, unknown>): AiHelpGuideContent | null {
  const replyType = obj['reply_type'];
  const methods = parseMethods(obj['methods']);

  const isGuide = replyType === 'guide' || (methods.length > 0 && typeof obj['intro'] === 'string');
  if (!isGuide) {
    return null;
  }

  const intro = typeof obj['intro'] === 'string' ? obj['intro'].trim() : '';
  if (!intro && methods.length === 0) {
    return null;
  }

  return {
    kind: 'guide',
    matchedId: typeof obj['matched_id'] === 'string' ? obj['matched_id'] : undefined,
    intro,
    methods,
    note: typeof obj['note'] === 'string' && obj['note'].trim() ? obj['note'].trim() : undefined,
    video: parseVideo(obj['video']),
    isAudio: obj['isAudio'] === true
  };
}

function parseVideo(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  const video = value.trim();
  return video || undefined;
}

function parseMethods(value: unknown): AiHelpGuideMethod[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((item) => parseMethod(item))
    .filter((method): method is AiHelpGuideMethod => method !== null);
}

function parseMethod(value: unknown): AiHelpGuideMethod | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const obj = value as Record<string, unknown>;
  const steps = parseSteps(obj['steps']);
  if (steps.length === 0) {
    return null;
  }

  const alternative =
    typeof obj['alternative'] === 'string' ? obj['alternative'].trim() || null : null;
  const label = typeof obj['label'] === 'string' ? obj['label'].trim() || null : null;
  const displayLabel = label ?? alternative;

  return {
    label: displayLabel,
    alternative,
    steps,
    video: parseVideo(obj['video'])
  };
}

function parseSteps(value: unknown): AiHelpGuideStep[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((item) => parseStep(item))
    .filter((step): step is AiHelpGuideStep => step !== null);
}

function parseStep(value: unknown): AiHelpGuideStep | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const obj = value as Record<string, unknown>;
  const text = typeof obj['text'] === 'string' ? obj['text'].trim() : '';
  if (!text) {
    return null;
  }

  const linkRaw = obj['link'];
  if (!linkRaw || typeof linkRaw !== 'object') {
    return { text };
  }

  const linkObj = linkRaw as Record<string, unknown>;
  const label = typeof linkObj['label'] === 'string' ? linkObj['label'].trim() : '';
  const url = typeof linkObj['url'] === 'string' ? linkObj['url'].trim() : '';
  if (!label || !url) {
    return { text };
  }

  return { text, link: { label, url } };
}

function extractPlainText(raw: unknown): string {
  if (raw == null) {
    return '';
  }

  if (typeof raw === 'string') {
    return raw.trim();
  }

  if (typeof raw !== 'object') {
    return String(raw);
  }

  const obj = raw as Record<string, unknown>;

  const direct =
    obj['answer'] ??
    obj['response'] ??
    obj['message'] ??
    obj['reply'] ??
    obj['text'];

  if (typeof direct === 'string' && direct.trim()) {
    return direct.trim();
  }

  const data = obj['data'];
  if (data && typeof data === 'object') {
    const nested = data as Record<string, unknown>;
    const nestedAnswer = nested['answer'] ?? nested['response'] ?? nested['message'];
    if (typeof nestedAnswer === 'string' && nestedAnswer.trim()) {
      return nestedAnswer.trim();
    }
  }

  const result = obj['result'];
  if (typeof result === 'string' && result.trim()) {
    return result.trim();
  }

  return '';
}
